import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { pm, getFileUrl } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { theme } from '../../theme';
import { SearchableMultiSelect } from '../../components/common/SearchableSelect';
import { MentionInput, CommentBody } from '../../components/common/MentionInput';

const PM_MANAGE_ROLES = ['project_manager','team_lead','super_admin','hr_admin','hr_head','ceo','cfo','cto'];

// ── Constants ────────────────────────────────────────────────────────────────

const PRIORITY_META = {
  urgent: { label:'Urgent',   color:'#DC2626', bg:'#FEF2F2', icon:'⚠' },
  high:   { label:'High',     color:'#EA580C', bg:'#FFF7ED', icon:'↑' },
  medium: { label:'Medium',   color:'#D97706', bg:'#FFFBEB', icon:'→' },
  low:    { label:'Low',      color:'#16A34A', bg:'#F0FDF4', icon:'↓' },
  none:   { label:'No Priority', color:'#9CA3AF', bg:'#F9FAFB', icon:'—' },
};

const TYPE_META = {
  task:        { label:'Task',        color:'#7C3AED', bg:'#EDE9FE', icon:'✓' },
  story:       { label:'Story',       color:'#2563EB', bg:'#DBEAFE', icon:'▤' },
  bug:         { label:'Bug',         color:'#BE185D', bg:'#FFE4E6', icon:'⬡' },
  feature:     { label:'Feature',     color:'#0891B2', bg:'#ECFEFF', icon:'★' },
  improvement: { label:'Improvement', color:'#059669', bg:'#ECFDF5', icon:'↗' },
  test:        { label:'Test',        color:'#7C3AED', bg:'#F5F3FF', icon:'⊡' },
};

const AV_COLORS = ['#EF4444','#F97316','#EAB308','#22C55E','#3B82F6','#8B5CF6','#EC4899','#06B6D4'];
function avatarColor(name) {
  if (!name) return '#9CA3AF';
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return AV_COLORS[Math.abs(h) % AV_COLORS.length];
}
function Avatar({ name, photo, size = 22 }) {
  const photoUrl = photo ? getFileUrl(photo) : null;
  const [imgError, setImgError] = useState(false);
  if (!name) return <div style={{ width:size, height:size, borderRadius:'50%', background:'#F3F4F6', flexShrink:0 }} />;
  if (photoUrl && !imgError) {
    return (
      <img src={photoUrl} title={name} alt={name}
        onError={() => setImgError(true)}
        style={{ width:size, height:size, borderRadius:'50%', objectFit:'cover', flexShrink:0, border:'1.5px solid #E5E7EB' }} />
    );
  }
  return (
    <div title={name} style={{ width:size, height:size, borderRadius:'50%', background:avatarColor(name),
      color:'#fff', fontSize:size*0.42+'px', fontWeight:800, display:'flex', alignItems:'center',
      justifyContent:'center', flexShrink:0, letterSpacing:0 }}>
      {name.charAt(0).toUpperCase()}
    </div>
  );
}

// ── Issue Row (list view) ────────────────────────────────────────────────────

function IssueRow({ issue, onOpen, onDelete }) {
  const [hov,     setHov]     = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const p = PRIORITY_META[issue.priority] || PRIORITY_META.none;
  const t = TYPE_META[issue.type]         || TYPE_META.task;
  const overdue = issue.due_date && !issue.completed_at && new Date(issue.due_date) < new Date();

  const handleMenuClick = (e, action) => {
    e.stopPropagation();
    setMenuOpen(false);
    if (action === 'open')   onOpen(issue);
    if (action === 'copyId') { navigator.clipboard?.writeText(issue.code); }
    if (action === 'delete' && onDelete) onDelete(issue.id);
  };

  return (
    <div onClick={() => onOpen(issue)}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => { setHov(false); setMenuOpen(false); }}
      style={{ display:'flex', alignItems:'center', gap:'0.625rem', padding:'0.55rem 1rem',
               background: hov ? '#F8FAFC' : '#fff', borderBottom:'1px solid #F1F5F9',
               cursor:'pointer', transition:'background 0.1s', position:'relative' }}>

      {/* Type badge */}
      <span style={{ background:t.bg, color:t.color, fontSize:'0.62rem', fontWeight:700,
                     padding:'1px 6px', borderRadius:4, flexShrink:0 }}>{t.icon}</span>

      {/* Code */}
      <span style={{ fontSize:'0.65rem', color:'#0D9488', fontFamily:'monospace', fontWeight:700,
                     letterSpacing:'0.04em', flexShrink:0, minWidth:52 }}>{issue.code}</span>

      {/* Title */}
      <span style={{ flex:1, fontSize:'0.83rem', fontWeight:500, color:'#111827',
                     overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
        {issue.title}
      </span>

      {/* Labels */}
      {issue.labels?.slice(0,2).map(l => (
        <span key={l.id} style={{ background:`${l.color}18`, color:l.color, fontSize:'0.6rem',
               fontWeight:700, padding:'1px 6px', borderRadius:9999, flexShrink:0 }}>
          {l.name}
        </span>
      ))}

      {/* Sprint/Module chips */}
      {issue.cycle_ids?.length > 0 && (
        <span style={{ fontSize:'0.6rem', color:'#7C3AED', background:'#EDE9FE',
                       padding:'1px 6px', borderRadius:9999, flexShrink:0 }}>
          🔄 {issue.cycle_ids.length}
        </span>
      )}
      {issue.module_ids?.length > 0 && (
        <span style={{ fontSize:'0.6rem', color:'#0891B2', background:'#ECFEFF',
                       padding:'1px 6px', borderRadius:9999, flexShrink:0 }}>
          ▦ {issue.module_ids.length}
        </span>
      )}

      {/* Sub-issues count */}
      {issue.sub_issues_count > 0 && (
        <span style={{ fontSize:'0.62rem', color:'#6B7280', background:'#F3F4F6',
                       padding:'1px 5px', borderRadius:4, flexShrink:0 }}>
          ⊙ {issue.sub_issues_count}
        </span>
      )}

      {/* Due date */}
      {issue.due_date && (
        <span style={{ fontSize:'0.65rem', color: overdue ? '#DC2626' : '#9CA3AF', flexShrink:0 }}>
          {new Date(issue.due_date).toLocaleDateString('en-IN', { day:'2-digit', month:'short' })}
        </span>
      )}

      {/* Priority */}
      <span title={p.label} style={{ width:6, height:6, borderRadius:'50%', background:p.color, flexShrink:0 }} />

      {/* Assignees */}
      <div style={{ display:'flex', gap:2, flexShrink:0 }}>
        {issue.assignees?.slice(0,3).map(a => (
          <Avatar key={a.employee_id} name={a.name} photo={a.profile_photo} size={20} />
        ))}
      </div>

      {/* Quick actions ⋯ */}
      {hov && (
        <div style={{ position:'relative', flexShrink:0 }} onClick={e => e.stopPropagation()}>
          <button
            onClick={e => { e.stopPropagation(); setMenuOpen(m => !m); }}
            style={{ background:'#F3F4F6', border:'none', borderRadius:5, padding:'2px 7px',
                     cursor:'pointer', color:'#374151', fontSize:'0.82rem', fontWeight:700,
                     lineHeight:1.2 }}>
            ⋯
          </button>
          {menuOpen && (
            <div style={{ position:'absolute', right:0, top:'calc(100% + 4px)', background:'#fff',
                          border:'1px solid #E4E4E7', borderRadius:8, boxShadow:'0 4px 16px rgba(0,0,0,0.12)',
                          zIndex:200, minWidth:140, overflow:'hidden' }}>
              {[
                { action:'open',   icon:'↗', label:'Open detail' },
                { action:'copyId', icon:'⊟', label:'Copy ID' },
                { action:'delete', icon:'✕', label:'Delete', danger:true },
              ].map(item => (
                <button key={item.action}
                  onClick={e => handleMenuClick(e, item.action)}
                  style={{ display:'flex', alignItems:'center', gap:8, width:'100%', padding:'7px 12px',
                           border:'none', background:'none', cursor:'pointer', textAlign:'left',
                           fontSize:'0.78rem', color: item.danger ? '#DC2626' : '#374151', fontWeight:500 }}
                  onMouseEnter={e => e.currentTarget.style.background = item.danger ? '#FEE2E2' : '#F8FAFC'}
                  onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                  <span>{item.icon}</span>
                  {item.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Issue Card (kanban view) ─────────────────────────────────────────────────

function IssueCard({ issue, onDragStart, onClick, isDragging }) {
  const [hov, setHov] = useState(false);
  const p = PRIORITY_META[issue.priority] || PRIORITY_META.none;
  const t = TYPE_META[issue.type]         || TYPE_META.task;
  const overdue = issue.due_date && !issue.completed_at && new Date(issue.due_date) < new Date();

  return (
    <div draggable onDragStart={(e) => onDragStart(e, issue)} onClick={() => onClick(issue)}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ background:'#fff', border:`1px solid ${hov ? '#C7C7CC' : '#E4E4E7'}`, borderRadius:8,
               padding:'0.6rem 0.75rem', marginBottom:'0.4rem', cursor: isDragging ? 'grabbing' : 'grab',
               opacity: isDragging ? 0.4 : 1, userSelect:'none',
               boxShadow: hov ? '0 4px 14px rgba(0,0,0,0.09)' : '0 1px 3px rgba(0,0,0,0.04)',
               transition:'box-shadow 0.12s, border-color 0.12s' }}>

      {/* Row 1: type + code + assignee */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'0.3rem' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'0.3rem' }}>
          <span style={{ background:t.bg, color:t.color, fontSize:'0.6rem', fontWeight:700,
                         padding:'1px 5px', borderRadius:4 }}>{t.icon}</span>
          <span style={{ fontSize:'0.62rem', color:'#0D9488', fontFamily:'monospace', fontWeight:700 }}>{issue.code}</span>
        </div>
        <div style={{ display:'flex', gap:2 }}>
          {issue.assignees?.slice(0,2).map(a => <Avatar key={a.employee_id} name={a.name} photo={a.profile_photo} size={18} />)}
        </div>
      </div>

      {/* Title */}
      <p style={{ margin:'0 0 0.4rem', fontSize:'0.81rem', fontWeight:500, color:'#111827', lineHeight:1.4 }}>
        {issue.title}
      </p>

      {/* Footer: labels + priority + due */}
      <div style={{ display:'flex', alignItems:'center', gap:'0.3rem', flexWrap:'wrap' }}>
        {issue.labels?.slice(0,2).map(l => (
          <span key={l.id} style={{ background:`${l.color}18`, color:l.color, fontSize:'0.58rem',
                 fontWeight:700, padding:'0 5px', borderRadius:9999 }}>{l.name}</span>
        ))}
        <span style={{ marginLeft:'auto', width:6, height:6, borderRadius:'50%', background:p.color, flexShrink:0 }} title={p.label} />
        {issue.due_date && (
          <span style={{ fontSize:'0.62rem', color: overdue ? '#DC2626' : '#9CA3AF' }}>
            {new Date(issue.due_date).toLocaleDateString('en-IN', { day:'2-digit', month:'short' })}
          </span>
        )}
        {issue.estimate && (
          <span style={{ fontSize:'0.6rem', color:'#6B7280', background:'#F3F4F6',
                         padding:'0 4px', borderRadius:3 }}>{issue.estimate}pt</span>
        )}
      </div>
    </div>
  );
}

// ── Activity Log ─────────────────────────────────────────────────────────────

function ActivityLog({ issueId }) {
  const [activities, setActivities] = useState([]);
  const [loading,    setLoading]    = useState(true);

  useEffect(() => {
    pm.getIssueActivities(issueId)
      .then(r => setActivities(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [issueId]);

  const fmt = (dt) => {
    if (!dt) return '';
    const d = new Date(dt);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  const describe = (act) => {
    if (act.comment) return act.comment;
    if (act.verb === 'created') return 'created this issue';
    if (act.field && act.old_value && act.new_value) return `changed ${act.field} from "${act.old_value}" to "${act.new_value}"`;
    if (act.field && act.new_value) return `set ${act.field} to "${act.new_value}"`;
    if (act.field && act.old_value) return `removed ${act.field} "${act.old_value}"`;
    return act.verb;
  };

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center', color: '#9CA3AF' }}>Loading…</div>;
  if (!activities.length) return <div style={{ padding: '2rem', textAlign: 'center', color: '#9CA3AF', fontSize: '0.83rem' }}>No activity recorded yet.</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {activities.map((act, idx) => (
        <div key={act.id} style={{ display: 'flex', gap: 10, padding: '0.5rem 0', borderBottom: idx < activities.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
          <div style={{ flexShrink: 0, marginTop: 2 }}>
            <Avatar name={act.actor_name} size={20} />
          </div>
          <div style={{ flex: 1 }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151' }}>{act.actor_name || 'System'}</span>
            {' '}
            <span style={{ fontSize: '0.75rem', color: '#6B7280' }}>{describe(act)}</span>
            <div style={{ fontSize: '0.65rem', color: '#9CA3AF', marginTop: 2 }}>{fmt(act.created_at)}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Issue Detail Panel ───────────────────────────────────────────────────────

function IssueDetailPanel({ issueId, projectId, states, labels, members, allIssues, onClose, onRefresh, canManage = false, currentEmpId = null }) {
  const [issue, setIssue]     = useState(null);
  const [tab, setTab]         = useState('details');
  const [saving, setSaving]   = useState(false);
  const [comment, setComment] = useState('');
  const [comments, setComments] = useState([]);
  const [subIssues, setSubIssues] = useState([]);
  const [newSubTitle, setNewSubTitle] = useState('');
  const [addingSub, setAddingSub]   = useState(false);
  const [relations, setRelations]       = useState({ blocking: [], blocked_by: [] });
  const [relSearch, setRelSearch]       = useState('');
  const [addingRel, setAddingRel]       = useState(false);
  const [relSearchBy, setRelSearchBy]   = useState('');
  const [addingRelBy, setAddingRelBy]   = useState(false);

  // Editable fields
  const [title, setTitle]         = useState('');
  const [description, setDesc]    = useState('');
  const [stateId, setStateId]     = useState('');
  const [priority, setPriority]   = useState('');
  const [type, setType]           = useState('');
  const [estimate, setEstimate]   = useState('');
  const [dueDate, setDueDate]     = useState('');
  const [startDate, setStartDate] = useState('');
  const [parentId, setParentId]   = useState('');
  const [assigneeIds, setAssigneeIds] = useState([]);
  const [labelIds, setLabelIds]   = useState([]);

  const load = useCallback(async () => {
    try {
      const r = await pm.getIssue(issueId);
      const i = r.data;
      setIssue(i);
      setTitle(i.title || '');
      setDesc(i.description || '');
      setStateId(i.state_id ? String(i.state_id) : '');
      setPriority(i.priority || 'none');
      setType(i.type || 'task');
      setEstimate(i.estimate ? String(i.estimate) : '');
      setDueDate(i.due_date || '');
      setStartDate(i.start_date || '');
      setParentId(i.parent_id ? String(i.parent_id) : '');
      setAssigneeIds(i.assignees?.map(a => a.employee_id) || []);
      setLabelIds(i.labels?.map(l => l.id) || []);
    } catch {}
  }, [issueId]);

  useEffect(() => { load(); }, [load]);

  const refreshRelations = async () => {
    try {
      const r = await pm.getIssueRelations(issueId);
      setRelations(r.data || { blocking: [], blocked_by: [] });
    } catch {}
  };

  useEffect(() => {
    if (!issueId) return;
    pm.getComments(issueId).then(r => setComments(r.data || [])).catch(() => {});
    pm.getIssues(projectId, { parent_id: issueId }).then(r => setSubIssues(r.data || [])).catch(() => {});
    refreshRelations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issueId, projectId]);

  const addRelation = async (toIssueId) => {
    try {
      await pm.addIssueRelation(issueId, { to_issue_id: toIssueId });
      const r = await pm.getIssueRelations(issueId);
      setRelations(r.data || { blocking: [], blocked_by: [] });
      setRelSearch('');
      setAddingRel(false);
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not add relation');
    }
  };

  // "issueId is blocked by fromIssueId" → fromIssueId blocks issueId
  const addBlockedByRelation = async (fromIssueId) => {
    try {
      await pm.addIssueRelation(fromIssueId, { to_issue_id: issueId });
      const r = await pm.getIssueRelations(issueId);
      setRelations(r.data || { blocking: [], blocked_by: [] });
      setRelSearchBy('');
      setAddingRelBy(false);
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not add relation');
    }
  };

  const removeRelation = async (rid) => {
    try {
      await pm.deleteIssueRelation(rid);
      const r = await pm.getIssueRelations(issueId);
      setRelations(r.data || { blocking: [], blocked_by: [] });
    } catch {}
  };

  const save = async () => {
    setSaving(true);
    try {
      let payload;
      if (!canEdit) {
        // Status-only update
        payload = { state_id: stateId ? Number(stateId) : null };
      } else if (!canManage) {
        // Creator: can edit fields but assignees locked to self
        payload = {
          title, description: description || null,
          state_id: stateId ? Number(stateId) : null,
          priority, type,
          estimate: estimate ? Number(estimate) : null,
          start_date: startDate || null,
          due_date: dueDate || null,
          parent_id: parentId ? Number(parentId) : null,
          assignee_ids: currentEmpId ? [currentEmpId] : assigneeIds,
          label_ids: labelIds,
        };
      } else {
        payload = {
          title, description: description || null,
          state_id: stateId ? Number(stateId) : null,
          priority, type,
          estimate: estimate ? Number(estimate) : null,
          start_date: startDate || null,
          due_date: dueDate || null,
          parent_id: parentId ? Number(parentId) : null,
          assignee_ids: assigneeIds,
          label_ids: labelIds,
        };
      }
      await pm.updateIssue(issueId, payload);
      onRefresh();
      onClose();
    } catch (err) { alert(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    try {
      await pm.addComment(issueId, { body: comment.trim() });
      setComment('');
      const r = await pm.getComments(issueId);
      setComments(r.data || []);
    } catch {}
  };

  const addSubIssue = async (e) => {
    e.preventDefault();
    if (!newSubTitle.trim()) return;
    try {
      const defaultState = states.find(s => s.is_default) || states[0];
      await pm.createIssue(projectId, {
        title: newSubTitle.trim(), type: 'task', priority: 'none',
        state_id: defaultState?.id, parent_id: issueId,
      });
      setNewSubTitle(''); setAddingSub(false);
      const r = await pm.getIssues(projectId, { parent_id: issueId });
      setSubIssues(r.data || []);
    } catch {}
  };

  // Access helpers derived from issue
  const isCreator = issue && currentEmpId !== null && issue.created_by_id === currentEmpId;
  const canEdit   = canManage || isCreator;   // can change any field
  const canStatus = true;                     // everyone can change status

  const toggleAssignee = (eid) => {
    if (!canManage) return; // only PM/TL can reassign
    setAssigneeIds(prev =>
      prev.includes(eid) ? prev.filter(id => id !== eid) : [...prev, eid]
    );
  };
  const toggleLabel = (lid) => {
    setLabelIds(prev =>
      prev.includes(lid) ? prev.filter(id => id !== lid) : [...prev, lid]
    );
  };

  if (!issue) return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.3)', zIndex:1000,
                  display:'flex', alignItems:'stretch', justifyContent:'flex-end' }}>
      <div style={{ width:560, background:'#fff', display:'flex', alignItems:'center',
                    justifyContent:'center', color:'#6B7280' }}>Loading…</div>
    </div>
  );

  const t  = TYPE_META[issue.type]         || TYPE_META.task;
  const pMeta = PRIORITY_META[issue.priority] || PRIORITY_META.none;
  const inp = { padding:'0.4rem 0.7rem', border:'1px solid #E4E4E7', borderRadius:7,
                fontSize:'0.83rem', background:'#fff', outline:'none', width:'100%',
                boxSizing:'border-box', color:'#111827', fontFamily:'inherit' };
  const lbl = { fontSize:'0.68rem', fontWeight:600, color:'#6B7280', textTransform:'uppercase',
                letterSpacing:'0.07em', display:'block', marginBottom:4 };

  const relCount = (relations.blocking?.length || 0) + (relations.blocked_by?.length || 0);
  const TABS = ['details','sub-issues','comments','activity','relations'];

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.35)', backdropFilter:'blur(2px)',
                  display:'flex', alignItems:'stretch', justifyContent:'flex-end', zIndex:1000 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <style>{`@keyframes slideIn{from{transform:translateX(30px);opacity:0}to{transform:translateX(0);opacity:1}}`}</style>
      <div style={{ background:'#fff', width:'100%', maxWidth:620, display:'flex',
                    flexDirection:'column', boxShadow:'-10px 0 40px rgba(0,0,0,0.14)',
                    animation:'slideIn 0.18s ease' }}>

        {/* Header */}
        <div style={{ padding:'1rem 1.25rem 0.75rem', borderBottom:'1px solid #F4F4F5', flexShrink:0 }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'0.5rem' }}>
            <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
              <span style={{ background:t.bg, color:t.color, padding:'2px 7px', borderRadius:5,
                             fontSize:'0.67rem', fontWeight:700 }}>{t.icon} {t.label}</span>
              <span style={{ fontSize:'0.68rem', color:'#0D9488', fontFamily:'monospace', fontWeight:700 }}>{issue.code}</span>
              <span style={{ display:'inline-flex', alignItems:'center', gap:3, fontSize:'0.67rem',
                             color:pMeta.color, fontWeight:600 }}>
                <span style={{ width:5, height:5, borderRadius:'50%', background:pMeta.color }} />
                {pMeta.label}
              </span>
            </div>
            <button onClick={onClose}
              style={{ background:'#F4F4F5', border:'none', borderRadius:7, width:28, height:28,
                       cursor:'pointer', color:'#6B7280', display:'flex', alignItems:'center',
                       justifyContent:'center', fontSize:'0.85rem' }}>✕</button>
          </div>
          <input value={title} onChange={e => setTitle(e.target.value)}
            style={{ width:'100%', border:'none', outline:'none', fontSize:'1rem', fontWeight:700,
                     color:'#111827', background:'transparent', fontFamily:'inherit', padding:0,
                     boxSizing:'border-box' }} />
        </div>

        {/* Tab nav */}
        <div style={{ display:'flex', borderBottom:'1px solid #F4F4F5', flexShrink:0,
                      background:'#FAFAFA', padding:'0 1.25rem' }}>
          {TABS.map(tb => (
            <button key={tb} onClick={() => setTab(tb)}
              style={{ background:'none', border:'none', padding:'0.5rem 0.8rem',
                       borderBottom:`2px solid ${tab===tb ? theme.primary : 'transparent'}`,
                       cursor:'pointer', fontSize:'0.78rem', fontWeight: tab===tb ? 700 : 500,
                       color: tab===tb ? theme.primary : '#6B7280', transition:'all 0.12s',
                       marginBottom:-1, textTransform:'capitalize' }}>
              {tb === 'sub-issues'  ? `Sub-issues${subIssues.length ? ` (${subIssues.length})` : ''}`
               : tb === 'comments'  ? `Comments${comments.length ? ` (${comments.length})` : ''}`
               : tb === 'activity'  ? 'Activity'
               : tb === 'relations' ? `Relations${relCount ? ` (${relCount})` : ''}`
               : 'Details'}
            </button>
          ))}
        </div>

        {/* Body */}
        <div style={{ flex:1, overflowY:'auto', padding:'1rem 1.25rem' }}>

          {/* ── Details tab ── */}
          {tab === 'details' && (
            <div style={{ display:'flex', flexDirection:'column', gap:'0.875rem' }}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.75rem' }}>
                <div>
                  <span style={lbl}>Status</span>
                  <select value={stateId} onChange={e => setStateId(e.target.value)} style={{ ...inp, cursor:'pointer' }}>
                    <option value="">— None —</option>
                    {states.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <span style={lbl}>Priority</span>
                  <select value={priority} onChange={e => setPriority(e.target.value)} style={{ ...inp, cursor:'pointer' }}>
                    {Object.entries(PRIORITY_META).map(([k, v]) => (
                      <option key={k} value={k}>{v.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <span style={lbl}>Type</span>
                  <select value={type} onChange={e => setType(e.target.value)} style={{ ...inp, cursor:'pointer' }}>
                    {Object.entries(TYPE_META).map(([k, v]) => (
                      <option key={k} value={k}>{v.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <span style={lbl}>Estimate (pts)</span>
                  <input type="number" min="0" value={estimate} onChange={e => setEstimate(e.target.value)}
                    placeholder="—" style={inp} />
                </div>
                <div>
                  <span style={lbl}>Start Date</span>
                  <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={inp} />
                </div>
                <div>
                  <span style={lbl}>Due Date</span>
                  <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} style={inp} />
                </div>
              </div>

              {/* Parent Issue */}
              <div>
                <span style={lbl}>Parent Issue</span>
                <select value={parentId} onChange={e => setParentId(e.target.value)}
                  style={{ ...inp, cursor:'pointer' }}>
                  <option value="">— None (top-level) —</option>
                  {(allIssues || [])
                    .filter(i => i.id !== issueId && !i.parent_id)
                    .map(i => (
                      <option key={i.id} value={i.id}>{i.code} — {i.title}</option>
                    ))}
                </select>
                {parentId && (
                  <div style={{ marginTop:4, fontSize:'0.7rem', color:'#6B7280' }}>
                    Parent: <span style={{ color:'#0D9488', fontFamily:'monospace', fontWeight:700 }}>
                      {(allIssues || []).find(i => i.id === Number(parentId))?.code}
                    </span>
                    {' — '}
                    {(allIssues || []).find(i => i.id === Number(parentId))?.title}
                  </div>
                )}
              </div>

              {/* Assignees */}
              <div>
                <span style={lbl}>Assignees</span>
                <SearchableMultiSelect
                  options={members.map(m => ({ value: m.employee_id, label: m.name }))}
                  values={assigneeIds}
                  onChange={setAssigneeIds}
                  placeholder="Select assignees…"
                  renderOption={(opt) => (
                    <span style={{ display:'flex', alignItems:'center', gap:6 }}>
                      <Avatar name={opt.label} size={18} />
                      {opt.label}
                    </span>
                  )}
                  renderTag={(opt) => (
                    <span style={{ display:'flex', alignItems:'center', gap:4 }}>
                      <Avatar name={opt.label} size={14} />
                      {opt.label}
                    </span>
                  )}
                />
              </div>

              {/* Labels */}
              <div>
                <span style={lbl}>Labels</span>
                <div style={{ display:'flex', flexWrap:'wrap', gap:'0.4rem' }}>
                  {labels.map(l => {
                    const active = labelIds.includes(l.id);
                    return (
                      <button key={l.id} onClick={() => toggleLabel(l.id)}
                        style={{ display:'flex', alignItems:'center', gap:5, padding:'3px 10px',
                                 borderRadius:9999, border:`1.5px solid ${active ? l.color : '#E4E4E7'}`,
                                 background: active ? `${l.color}18` : '#fff',
                                 cursor:'pointer', fontSize:'0.73rem', fontWeight: active ? 700 : 400,
                                 color: active ? l.color : '#374151' }}>
                        <span style={{ width:7, height:7, borderRadius:'50%', background:l.color }} />
                        {l.name}
                        {active && <span>✓</span>}
                      </button>
                    );
                  })}
                  {labels.length === 0 && <span style={{ fontSize:'0.78rem', color:'#9CA3AF' }}>No labels configured</span>}
                </div>
              </div>

              {/* Blocked by */}
              <div>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'0.35rem' }}>
                  <span style={lbl}>Blocked by</span>
                  <button type="button" onClick={() => setAddingRelBy(v => !v)}
                    style={{ fontSize:'0.68rem', background: addingRelBy ? '#E5E7EB' : '#FEE2E2',
                             color: addingRelBy ? '#374151' : '#DC2626', border:'none', borderRadius:5,
                             padding:'1px 8px', cursor:'pointer', fontWeight:600 }}>
                    {addingRelBy ? 'Cancel' : '+ Add'}
                  </button>
                </div>
                {addingRelBy && (
                  <div style={{ marginBottom:'0.5rem', position:'relative' }}>
                    <input
                      autoFocus
                      value={relSearchBy}
                      onChange={e => setRelSearchBy(e.target.value)}
                      placeholder="Search issues that block this…"
                      style={{ ...inp, marginBottom:'0.25rem' }}
                    />
                    {relSearchBy && (
                      <div style={{ position:'absolute', top:'calc(100% + 2px)', left:0, right:0, zIndex:20,
                                    background:'#fff', border:'1px solid #E4E4E7', borderRadius:7,
                                    maxHeight:150, overflowY:'auto', boxShadow:'0 4px 12px rgba(0,0,0,0.08)' }}>
                        {allIssues
                          .filter(i =>
                            i.id !== issueId &&
                            !relations.blocked_by?.find(r => r.issue.id === i.id) &&
                            (i.title.toLowerCase().includes(relSearchBy.toLowerCase()) ||
                             (i.code || '').toLowerCase().includes(relSearchBy.toLowerCase()))
                          )
                          .slice(0, 20)
                          .map(i => (
                            <div key={i.id}
                              onMouseDown={e => { e.preventDefault(); addBlockedByRelation(i.id); }}
                              style={{ padding:'0.4rem 0.75rem', cursor:'pointer', fontSize:'0.8rem',
                                       display:'flex', gap:'0.4rem', alignItems:'center',
                                       borderBottom:'1px solid #F4F4F5' }}
                              onMouseEnter={e => e.currentTarget.style.background='#F9FAFB'}
                              onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                              <span style={{ fontSize:'0.65rem', color:'#0D9488', fontFamily:'monospace',
                                             fontWeight:700, flexShrink:0 }}>{i.code}</span>
                              <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                                {i.title}
                              </span>
                            </div>
                          ))}
                        {allIssues.filter(i => i.id !== issueId &&
                          !relations.blocked_by?.find(r => r.issue.id === i.id) &&
                          (i.title.toLowerCase().includes(relSearchBy.toLowerCase()) ||
                           (i.code || '').toLowerCase().includes(relSearchBy.toLowerCase()))).length === 0 && (
                          <div style={{ padding:'0.5rem 0.75rem', fontSize:'0.78rem', color:'#9CA3AF' }}>
                            No matching issues
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {relations.blocked_by?.length === 0 && !addingRelBy
                  ? <span style={{ fontSize:'0.78rem', color:'#9CA3AF' }}>None</span>
                  : (
                    <div style={{ display:'flex', flexWrap:'wrap', gap:'0.3rem' }}>
                      {relations.blocked_by?.map(r => (
                        <span key={r.relation_id}
                          style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'2px 8px',
                                   background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:6,
                                   fontSize:'0.72rem', color:'#DC2626', fontWeight:600 }}>
                          <span style={{ fontFamily:'monospace' }}>{r.issue.identifier}</span>
                          <span style={{ color:'#374151', fontWeight:400 }}>
                            {r.issue.title.length > 22 ? r.issue.title.slice(0,22)+'…' : r.issue.title}
                          </span>
                          <button type="button" onClick={() => removeRelation(r.relation_id)}
                            style={{ background:'none', border:'none', cursor:'pointer', color:'#9CA3AF',
                                     padding:0, lineHeight:1, fontSize:'0.75rem' }}>✕</button>
                        </span>
                      ))}
                    </div>
                  )
                }
              </div>

              {/* Description */}
              <div>
                <span style={lbl}>Description</span>
                <textarea value={description} onChange={e => setDesc(e.target.value)}
                  placeholder="Add a description…" rows={4}
                  style={{ ...inp, resize:'vertical', lineHeight:1.6 }} />
              </div>

              {/* Metadata */}
              {(issue.cycle_ids?.length > 0 || issue.module_ids?.length > 0) && (
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.75rem' }}>
                  {issue.cycle_ids?.length > 0 && (
                    <div>
                      <span style={lbl}>Sprints</span>
                      <span style={{ fontSize:'0.8rem', color:'#374151' }}>{issue.cycle_ids.length} sprint(s)</span>
                    </div>
                  )}
                  {issue.module_ids?.length > 0 && (
                    <div>
                      <span style={lbl}>Modules</span>
                      <span style={{ fontSize:'0.8rem', color:'#374151' }}>{issue.module_ids.length} module(s)</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Sub-issues tab ── */}
          {tab === 'sub-issues' && (
            <div>
              {/* Parent row */}
              <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', padding:'0.5rem 0.75rem',
                            background:'#F8FAFC', borderRadius:8, marginBottom:'0.5rem',
                            border:'1px solid #E4E4E7' }}>
                <span style={{ background:t.bg, color:t.color, fontSize:'0.6rem', fontWeight:700,
                               padding:'1px 5px', borderRadius:4 }}>{t.icon}</span>
                <span style={{ fontSize:'0.65rem', color:'#0D9488', fontFamily:'monospace', fontWeight:700 }}>{issue.code}</span>
                <span style={{ fontSize:'0.82rem', fontWeight:600, color:'#111827', flex:1 }}>{issue.title}</span>
              </div>

              {/* Children */}
              {subIssues.map((sub, idx) => {
                const st = TYPE_META[sub.type] || TYPE_META.task;
                const isLast = idx === subIssues.length - 1 && !addingSub;
                return (
                  <div key={sub.id} style={{ display:'flex', alignItems:'stretch' }}>
                    <div style={{ width:32, flexShrink:0, position:'relative' }}>
                      <div style={{ position:'absolute', left:15, top:0, bottom: isLast ? '50%' : 0, width:1, background:'#E4E4E7' }} />
                      <div style={{ position:'absolute', left:15, top:'50%', width:12, height:1, background:'#E4E4E7' }} />
                    </div>
                    <div style={{ flex:1, display:'flex', alignItems:'center', gap:'0.5rem',
                                  padding:'0.4rem 0.75rem', borderRadius:7, marginBottom:2,
                                  background:'#fff', border:'1px solid transparent' }}
                      onMouseEnter={e => { e.currentTarget.style.background='#F8FAFC'; e.currentTarget.style.borderColor='#E4E4E7'; }}
                      onMouseLeave={e => { e.currentTarget.style.background='#fff'; e.currentTarget.style.borderColor='transparent'; }}>
                      <span style={{ background:st.bg, color:st.color, fontSize:'0.6rem', fontWeight:700,
                                     padding:'1px 5px', borderRadius:4 }}>{st.icon}</span>
                      <span style={{ fontSize:'0.63rem', color:'#0D9488', fontFamily:'monospace', fontWeight:700 }}>{sub.code}</span>
                      <span style={{ flex:1, fontSize:'0.8rem', color:'#111827' }}>{sub.title}</span>
                      {sub.state && (
                        <span style={{ fontSize:'0.62rem', color:sub.state.color, fontWeight:600,
                                       display:'flex', alignItems:'center', gap:3 }}>
                          <span style={{ width:5, height:5, borderRadius:'50%', background:sub.state.color }} />
                          {sub.state.name}
                        </span>
                      )}
                      <div style={{ display:'flex', gap:2 }}>
                        {sub.assignees?.slice(0,2).map(a => <Avatar key={a.employee_id} name={a.name} photo={a.profile_photo} size={16} />)}
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Add sub-issue inline */}
              {addingSub && (
                <div style={{ display:'flex', alignItems:'stretch' }}>
                  <div style={{ width:32, flexShrink:0, position:'relative' }}>
                    <div style={{ position:'absolute', left:15, top:0, bottom:'50%', width:1, background:'#E4E4E7' }} />
                    <div style={{ position:'absolute', left:15, top:'50%', width:12, height:1, background:'#E4E4E7' }} />
                  </div>
                  <form onSubmit={addSubIssue} style={{ flex:1, display:'flex', gap:'0.4rem', padding:'0.3rem 0' }}>
                    <input autoFocus value={newSubTitle} onChange={e => setNewSubTitle(e.target.value)}
                      placeholder="Sub-issue title…"
                      style={{ flex:1, padding:'0.35rem 0.6rem', border:'1px solid #C4B5FD',
                               borderRadius:6, fontSize:'0.8rem', outline:'none' }} />
                    <button type="submit" style={{ background:'#5B21B6', color:'#fff', border:'none',
                      borderRadius:6, padding:'0.35rem 0.75rem', cursor:'pointer', fontSize:'0.72rem', fontWeight:700 }}>
                      Add
                    </button>
                    <button type="button" onClick={() => setAddingSub(false)}
                      style={{ padding:'0.35rem 0.5rem', border:'1px solid #E4E4E7', background:'#fff',
                               borderRadius:6, cursor:'pointer', fontSize:'0.72rem', color:'#6B7280' }}>✕</button>
                  </form>
                </div>
              )}

              {!addingSub && (
                <button onClick={() => setAddingSub(true)}
                  style={{ marginLeft:32, marginTop:4, background:'none', border:'1px dashed #D1D5DB',
                           borderRadius:6, padding:'4px 12px', cursor:'pointer', fontSize:'0.75rem',
                           color:'#6B7280', fontWeight:500 }}>
                  + Add Sub-issue
                </button>
              )}
              {subIssues.length === 0 && !addingSub && (
                <p style={{ marginLeft:32, marginTop:8, fontSize:'0.78rem', color:'#9CA3AF' }}>
                  No sub-issues yet.
                </p>
              )}
            </div>
          )}

          {/* ── Comments tab ── */}
          {tab === 'comments' && (
            <div style={{ display:'flex', flexDirection:'column', gap:'0.75rem' }}>
              {comments.map(c => (
                <div key={`${c.source||'issue'}-${c.id}`} style={{ background: c.source === 'bug' ? '#FFF7ED' : '#F8FAFC', borderRadius:8, padding:'0.75rem', border: c.source === 'bug' ? '1px solid #FED7AA' : '1px solid #F1F5F9' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', marginBottom:'0.4rem' }}>
                    <Avatar name={c.author_name} size={20} />
                    <span style={{ fontSize:'0.75rem', fontWeight:600, color:'#374151' }}>{c.author_name || 'Unknown'}</span>
                    {c.source === 'bug' && <span style={{ fontSize:'0.65rem', background:'#FED7AA', color:'#9A3412', borderRadius:4, padding:'1px 6px', fontWeight:600 }}>Bug</span>}
                    <span style={{ fontSize:'0.65rem', color:'#9CA3AF', marginLeft:'auto' }}>
                      {c.created_at ? new Date(c.created_at).toLocaleDateString('en-IN', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' }) : ''}
                    </span>
                  </div>
                  <CommentBody body={c.body} style={{ fontSize:'0.82rem' }} />
                </div>
              ))}
              {comments.length === 0 && (
                <p style={{ textAlign:'center', color:'#9CA3AF', fontSize:'0.82rem', padding:'1rem 0' }}>No comments yet.</p>
              )}
              <form onSubmit={submitComment} style={{ display:'flex', gap:'0.5rem', marginTop:'0.25rem', alignItems:'flex-end' }}>
                <MentionInput
                  value={comment}
                  onChange={setComment}
                  members={members}
                  rows={2}
                  placeholder="Add a comment… type @ to mention"
                  style={{ padding:'0.45rem 0.75rem', border:'1px solid #E4E4E7', borderRadius:8, fontSize:'0.82rem', fontFamily:'inherit', outline:'none' }}
                />
                <button type="submit"
                  style={{ background:theme.primary, color:'#fff', border:'none', borderRadius:8,
                           padding:'0.45rem 1rem', cursor:'pointer', fontWeight:700, fontSize:'0.82rem', flexShrink:0 }}>
                  Send
                </button>
              </form>
            </div>
          )}

          {/* ── Activity tab ── */}
          {tab === 'activity' && (
            <ActivityLog issueId={issueId} />
          )}

          {/* ── Relations tab ── */}
          {tab === 'relations' && (
            <div>
              {/* Blocking section */}
              <div style={{ marginBottom:'1.25rem' }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                              marginBottom:'0.5rem' }}>
                  <span style={{ fontSize:'0.78rem', fontWeight:700, color:'#374151' }}>
                    Blocks ({relations.blocking?.length || 0})
                  </span>
                  <button
                    onClick={() => setAddingRel(v => !v)}
                    style={{ fontSize:'0.72rem', background: theme.primary, color:'#fff', border:'none',
                             borderRadius:6, padding:'2px 10px', cursor:'pointer', fontWeight:600 }}>
                    + Add
                  </button>
                </div>

                {addingRel && (
                  <div style={{ marginBottom:'0.75rem' }}>
                    <input
                      autoFocus
                      value={relSearch}
                      onChange={e => setRelSearch(e.target.value)}
                      placeholder="Search issues to block…"
                      style={{ width:'100%', boxSizing:'border-box', padding:'0.4rem 0.6rem',
                               border:'1px solid #E4E4E7', borderRadius:7, fontSize:'0.82rem',
                               outline:'none', marginBottom:'0.3rem' }}
                    />
                    <div style={{ maxHeight:160, overflowY:'auto', border:'1px solid #E4E4E7',
                                  borderRadius:7, background:'#fff' }}>
                      {allIssues
                        .filter(i =>
                          i.id !== issueId &&
                          !relations.blocking?.find(r => r.issue.id === i.id) &&
                          (relSearch === '' ||
                            i.title.toLowerCase().includes(relSearch.toLowerCase()) ||
                            (i.code || '').toLowerCase().includes(relSearch.toLowerCase()))
                        )
                        .slice(0, 20)
                        .map(i => (
                          <div key={i.id}
                            onClick={() => addRelation(i.id)}
                            style={{ padding:'0.4rem 0.75rem', cursor:'pointer', fontSize:'0.8rem',
                                     display:'flex', gap:'0.4rem', alignItems:'center',
                                     borderBottom:'1px solid #F4F4F5' }}
                            onMouseEnter={e => e.currentTarget.style.background='#F9FAFB'}
                            onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                            <span style={{ fontSize:'0.65rem', color:'#0D9488', fontFamily:'monospace',
                                           fontWeight:700, flexShrink:0 }}>{i.code}</span>
                            <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                              {i.title}
                            </span>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {relations.blocking?.length === 0 && !addingRel && (
                  <p style={{ fontSize:'0.78rem', color:'#9CA3AF', margin:0 }}>No blocking relations.</p>
                )}
                {relations.blocking?.map(r => (
                  <div key={r.relation_id}
                    style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                             padding:'0.35rem 0.6rem', background:'#FFF7ED', borderRadius:7,
                             border:'1px solid #FED7AA', marginBottom:'0.35rem' }}>
                    <div style={{ display:'flex', gap:'0.4rem', alignItems:'center', overflow:'hidden' }}>
                      <span style={{ fontSize:'0.65rem', color:'#EA580C', fontFamily:'monospace',
                                     fontWeight:700, flexShrink:0 }}>{r.issue.identifier}</span>
                      <span style={{ fontSize:'0.78rem', color:'#374151', overflow:'hidden',
                                     textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.issue.title}</span>
                    </div>
                    <button onClick={() => removeRelation(r.relation_id)}
                      style={{ background:'none', border:'none', cursor:'pointer', color:'#9CA3AF',
                               fontSize:'0.75rem', flexShrink:0, padding:'0 2px' }}
                      title="Remove relation">✕</button>
                  </div>
                ))}
              </div>

              {/* Blocked by section */}
              <div>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                              marginBottom:'0.5rem' }}>
                  <span style={{ fontSize:'0.78rem', fontWeight:700, color:'#374151' }}>
                    Blocked by ({relations.blocked_by?.length || 0})
                  </span>
                  <button
                    onClick={() => setAddingRelBy(v => !v)}
                    style={{ fontSize:'0.72rem', background:'#DC2626', color:'#fff', border:'none',
                             borderRadius:6, padding:'2px 10px', cursor:'pointer', fontWeight:600 }}>
                    + Add
                  </button>
                </div>

                {addingRelBy && (
                  <div style={{ marginBottom:'0.75rem' }}>
                    <input
                      autoFocus
                      value={relSearchBy}
                      onChange={e => setRelSearchBy(e.target.value)}
                      placeholder="Search issues that block this…"
                      style={{ width:'100%', boxSizing:'border-box', padding:'0.4rem 0.6rem',
                               border:'1px solid #E4E4E7', borderRadius:7, fontSize:'0.82rem',
                               outline:'none', marginBottom:'0.3rem' }}
                    />
                    <div style={{ maxHeight:160, overflowY:'auto', border:'1px solid #E4E4E7',
                                  borderRadius:7, background:'#fff' }}>
                      {allIssues
                        .filter(i =>
                          i.id !== issueId &&
                          !relations.blocked_by?.find(r => r.issue.id === i.id) &&
                          (relSearchBy === '' ||
                            i.title.toLowerCase().includes(relSearchBy.toLowerCase()) ||
                            (i.code || '').toLowerCase().includes(relSearchBy.toLowerCase()))
                        )
                        .slice(0, 20)
                        .map(i => (
                          <div key={i.id}
                            onClick={() => addBlockedByRelation(i.id)}
                            style={{ padding:'0.4rem 0.75rem', cursor:'pointer', fontSize:'0.8rem',
                                     display:'flex', gap:'0.4rem', alignItems:'center',
                                     borderBottom:'1px solid #F4F4F5' }}
                            onMouseEnter={e => e.currentTarget.style.background='#F9FAFB'}
                            onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                            <span style={{ fontSize:'0.65rem', color:'#0D9488', fontFamily:'monospace',
                                           fontWeight:700, flexShrink:0 }}>{i.code}</span>
                            <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                              {i.title}
                            </span>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {relations.blocked_by?.length === 0 && !addingRelBy && (
                  <p style={{ fontSize:'0.78rem', color:'#9CA3AF', margin:0 }}>No blocking dependencies.</p>
                )}
                {relations.blocked_by?.map(r => (
                  <div key={r.relation_id}
                    style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                             padding:'0.35rem 0.6rem', background:'#FEF2F2', borderRadius:7,
                             border:'1px solid #FECACA', marginBottom:'0.35rem' }}>
                    <div style={{ display:'flex', gap:'0.4rem', alignItems:'center', overflow:'hidden' }}>
                      <span style={{ fontSize:'0.65rem', color:'#DC2626', fontFamily:'monospace',
                                     fontWeight:700, flexShrink:0 }}>{r.issue.identifier}</span>
                      <span style={{ fontSize:'0.78rem', color:'#374151', overflow:'hidden',
                                     textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.issue.title}</span>
                    </div>
                    <button onClick={() => removeRelation(r.relation_id)}
                      style={{ background:'none', border:'none', cursor:'pointer', color:'#9CA3AF',
                               fontSize:'0.75rem', flexShrink:0, padding:'0 2px' }}
                      title="Remove relation">✕</button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding:'0.75rem 1.25rem', borderTop:'1px solid #F4F4F5', display:'flex',
                      gap:'0.5rem', justifyContent:'flex-end', background:'#FAFAFA', flexShrink:0 }}>
          <button onClick={onClose}
            style={{ padding:'0.45rem 1rem', border:'1px solid #E4E4E7', background:'#fff',
                     borderRadius:8, cursor:'pointer', fontWeight:600, fontSize:'0.83rem', color:'#374151' }}>
            Cancel
          </button>
          <button onClick={save} disabled={saving}
            style={{ padding:'0.45rem 1.25rem', background: saving ? '#D1D5DB' : theme.primary,
                     color:'#fff', border:'none', borderRadius:8, fontWeight:700, fontSize:'0.83rem',
                     cursor: saving ? 'not-allowed' : 'pointer',
                     boxShadow: saving ? 'none' : `0 2px 8px ${theme.primary}40` }}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Create Issue Modal ───────────────────────────────────────────────────────

const TITLE_MAX = 500;
const DESC_MAX  = 10000;

function CreateIssueModal({ projectId, states, labels, members, allIssues, onClose, onCreated, canManage = false, currentEmpId = null }) {
  const [title, setTitle]             = useState('');
  const [description, setDescription] = useState('');
  const [type, setType]               = useState('task');
  const [priority, setPriority] = useState('none');
  const [stateId, setStateId]   = useState(() => states.find(s => s.is_default)?.id || states[0]?.id || '');
  // Non-PM/TL employees are always auto-assigned to themselves
  const [assigneeIds, setAssigneeIds] = useState(() => (!canManage && currentEmpId) ? [currentEmpId] : []);
  const [labelIds, setLabelIds]   = useState([]);
  const [startDate, setStartDate] = useState('');
  const [dueDate, setDueDate]     = useState('');
  const [estimate, setEstimate]   = useState('');
  const [parentId, setParentId]       = useState('');
  const [moduleId, setModuleId]       = useState('');
  const [modules,  setModules]        = useState([]);
  const [blockedByIds, setBlockedByIds] = useState([]);
  const [blockedBySearch, setBlockedBySearch] = useState('');
  const [saving, setSaving]           = useState(false);

  useEffect(() => {
    pm.getModules(projectId).then(r => setModules(r.data || [])).catch(() => {});
  }, [projectId]);

  // When type changes, reset context-specific selectors
  const handleTypeChange = (newType) => {
    setType(newType);
    if (!['task', 'test', 'bug'].includes(newType)) setParentId('');
    if (!['story', 'feature', 'improvement'].includes(newType)) setModuleId('');
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    if (title.trim().length > TITLE_MAX) return;
    if (description.length > DESC_MAX) return;
    setSaving(true);
    try {
      const res = await pm.createIssue(projectId, {
        title: title.trim(), type, priority,
        description: description.trim() || null,
        state_id: stateId ? Number(stateId) : null,
        start_date: startDate || null,
        due_date: dueDate || null,
        estimate: estimate ? Number(estimate) : null,
        parent_id: parentId ? Number(parentId) : null,
        assignee_ids: assigneeIds,
        label_ids: labelIds,
      });
      // Link to module if selected (for story/feature types)
      if (moduleId && res?.data?.id) {
        await pm.addIssuesToModule(Number(moduleId), { issue_ids: [res.data.id] });
      }
      // Create blocked-by relations: each blocker → blocks → new issue
      if (res?.data?.id && blockedByIds.length > 0) {
        await Promise.allSettled(
          blockedByIds.map(bid => pm.addIssueRelation(bid, { to_issue_id: res.data.id }))
        );
      }
      onCreated();
      onClose();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const inp = { padding:'0.4rem 0.7rem', border:'1px solid #E4E4E7', borderRadius:7,
                fontSize:'0.83rem', outline:'none', background:'#fff', width:'100%',
                boxSizing:'border-box', color:'#111827', fontFamily:'inherit' };
  const lbl = { fontSize:'0.68rem', fontWeight:600, color:'#6B7280', textTransform:'uppercase',
                letterSpacing:'0.07em', display:'block', marginBottom:4 };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', backdropFilter:'blur(2px)',
                  display:'flex', alignItems:'center', justifyContent:'center', zIndex:1100 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background:'#fff', borderRadius:12, width:'100%', maxWidth:560,
                    boxShadow:'0 20px 60px rgba(0,0,0,0.18)',
                    maxHeight:'92vh', display:'flex', flexDirection:'column' }}>
        <div style={{ padding:'1.25rem 1.5rem 1rem', borderBottom:'1px solid #F4F4F5', flexShrink:0 }}>
          <h3 style={{ margin:0, fontSize:'1rem', fontWeight:700, color:'#111827' }}>Create Issue</h3>
        </div>
        <form onSubmit={submit} style={{ flex:1, overflowY:'auto', padding:'1.25rem 1.5rem', display:'flex', flexDirection:'column', gap:'0.875rem' }}>
          {/* Title */}
          <div>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:4 }}>
              <span style={lbl}>Title *</span>
              <span style={{ fontSize:'0.65rem', color: title.length > TITLE_MAX ? '#DC2626' : '#9CA3AF' }}>
                {title.length}/{TITLE_MAX}
              </span>
            </div>
            <input autoFocus value={title} onChange={e => setTitle(e.target.value)}
              placeholder="Issue title…" required maxLength={TITLE_MAX}
              style={{ ...inp, borderColor: title.length > TITLE_MAX ? '#DC2626' : undefined }} />
            {title.length > TITLE_MAX && (
              <p style={{ margin:'2px 0 0', fontSize:'0.72rem', color:'#DC2626' }}>
                Title exceeds {TITLE_MAX} characters
              </p>
            )}
          </div>

          {/* Description */}
          <div>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:4 }}>
              <span style={lbl}>Description</span>
              <span style={{ fontSize:'0.65rem', color: description.length > DESC_MAX ? '#DC2626' : '#9CA3AF' }}>
                {description.length}/{DESC_MAX}
              </span>
            </div>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Describe the issue, steps to reproduce, acceptance criteria…"
              rows={4}
              maxLength={DESC_MAX}
              style={{
                ...inp, resize:'vertical', minHeight:88,
                borderColor: description.length > DESC_MAX ? '#DC2626' : undefined,
                fontFamily:'inherit',
              }}
            />
            {description.length > DESC_MAX && (
              <p style={{ margin:'2px 0 0', fontSize:'0.72rem', color:'#DC2626' }}>
                Description exceeds {DESC_MAX} characters
              </p>
            )}
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'0.75rem' }}>
            <div>
              <span style={lbl}>Type</span>
              {canManage ? (
                <select value={type} onChange={e => handleTypeChange(e.target.value)} style={{ ...inp, cursor:'pointer' }}>
                  {Object.entries(TYPE_META).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              ) : (
                <div style={{ ...inp, background:'#F9FAFB', color:'#6B7280', cursor:'default' }}>Task</div>
              )}
            </div>
            <div>
              <span style={lbl}>Priority</span>
              <select value={priority} onChange={e => setPriority(e.target.value)} style={{ ...inp, cursor:'pointer' }}>
                {Object.entries(PRIORITY_META).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <span style={lbl}>Status</span>
              <select value={stateId} onChange={e => setStateId(e.target.value)} style={{ ...inp, cursor:'pointer' }}>
                {states.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>

          {/* Module selector — shown for story / feature / improvement */}
          {['story','feature','improvement'].includes(type) && modules.length > 0 && (
            <div>
              <span style={lbl}>Module</span>
              <select value={moduleId} onChange={e => setModuleId(e.target.value)} style={{ ...inp, cursor:'pointer' }}>
                <option value="">— No module —</option>
                {modules.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
          )}

          {/* Parent story selector — shown for task / test / bug */}
          {['task','test','bug'].includes(type) && (
            <div>
              <span style={lbl}>Parent Story / Feature</span>
              <select value={parentId} onChange={e => setParentId(e.target.value)} style={{ ...inp, cursor:'pointer' }}>
                <option value="">— No parent (top-level) —</option>
                {(allIssues || [])
                  .filter(i => ['story','feature','improvement'].includes(i.type) && !i.parent_id)
                  .map(i => (
                    <option key={i.id} value={i.id}>{i.code} — {i.title}</option>
                  ))}
              </select>
            </div>
          )}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'0.75rem' }}>
            <div>
              <span style={lbl}>Start Date</span>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={inp} />
            </div>
            <div>
              <span style={lbl}>Due Date</span>
              <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} style={inp} />
            </div>
            <div>
              <span style={lbl}>Estimate (pts)</span>
              <input type="number" min="0" value={estimate} onChange={e => setEstimate(e.target.value)}
                placeholder="—" style={inp} />
            </div>
          </div>
          {members.length > 0 && canManage && (
            <div>
              <span style={lbl}>Assignees</span>
              <SearchableMultiSelect
                options={members.map(m => ({ value: m.employee_id, label: m.name }))}
                values={assigneeIds}
                onChange={setAssigneeIds}
                placeholder="Select assignees…"
                renderOption={(opt) => (
                  <span style={{ display:'flex', alignItems:'center', gap:6 }}>
                    <Avatar name={opt.label} size={18} />
                    {opt.label}
                  </span>
                )}
                renderTag={(opt) => (
                  <span style={{ display:'flex', alignItems:'center', gap:4 }}>
                    <Avatar name={opt.label} size={14} />
                    {opt.label}
                  </span>
                )}
              />
            </div>
          )}
          {/* Blocked by */}
          {allIssues?.length > 0 && (
            <div>
              <span style={lbl}>Blocked by</span>
              {/* Selected blockers */}
              {blockedByIds.length > 0 && (
                <div style={{ display:'flex', flexWrap:'wrap', gap:'0.3rem', marginBottom:'0.4rem' }}>
                  {blockedByIds.map(bid => {
                    const iss = allIssues.find(i => i.id === bid);
                    return iss ? (
                      <span key={bid}
                        style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'2px 8px',
                                 background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:6,
                                 fontSize:'0.72rem', color:'#DC2626', fontWeight:600 }}>
                        <span style={{ fontFamily:'monospace' }}>{iss.code}</span>
                        <span style={{ color:'#374151', fontWeight:400 }}>{iss.title.length > 24 ? iss.title.slice(0,24)+'…' : iss.title}</span>
                        <button type="button"
                          onClick={() => setBlockedByIds(prev => prev.filter(id => id !== bid))}
                          style={{ background:'none', border:'none', cursor:'pointer', color:'#9CA3AF',
                                   padding:0, lineHeight:1, fontSize:'0.75rem' }}>✕</button>
                      </span>
                    ) : null;
                  })}
                </div>
              )}
              <div style={{ position:'relative' }}>
                <input
                  type="text"
                  value={blockedBySearch}
                  onChange={e => setBlockedBySearch(e.target.value)}
                  placeholder="Search and add blockers…"
                  style={{ ...inp }}
                />
                {blockedBySearch && (
                  <div style={{ position:'absolute', top:'calc(100% + 2px)', left:0, right:0, zIndex:20,
                                background:'#fff', border:'1px solid #E4E4E7', borderRadius:7,
                                maxHeight:160, overflowY:'auto', boxShadow:'0 4px 12px rgba(0,0,0,0.08)' }}>
                    {allIssues
                      .filter(i =>
                        !blockedByIds.includes(i.id) &&
                        (i.title.toLowerCase().includes(blockedBySearch.toLowerCase()) ||
                         (i.code || '').toLowerCase().includes(blockedBySearch.toLowerCase()))
                      )
                      .slice(0, 20)
                      .map(i => (
                        <div key={i.id}
                          onMouseDown={e => { e.preventDefault(); setBlockedByIds(prev => [...prev, i.id]); setBlockedBySearch(''); }}
                          style={{ padding:'0.4rem 0.75rem', cursor:'pointer', fontSize:'0.8rem',
                                   display:'flex', gap:'0.4rem', alignItems:'center',
                                   borderBottom:'1px solid #F4F4F5' }}
                          onMouseEnter={e => e.currentTarget.style.background='#F9FAFB'}
                          onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                          <span style={{ fontSize:'0.65rem', color:'#0D9488', fontFamily:'monospace',
                                         fontWeight:700, flexShrink:0 }}>{i.code}</span>
                          <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                            {i.title}
                          </span>
                        </div>
                      ))}
                    {allIssues.filter(i => !blockedByIds.includes(i.id) &&
                      (i.title.toLowerCase().includes(blockedBySearch.toLowerCase()) ||
                       (i.code || '').toLowerCase().includes(blockedBySearch.toLowerCase()))).length === 0 && (
                      <div style={{ padding:'0.5rem 0.75rem', fontSize:'0.78rem', color:'#9CA3AF' }}>
                        No matching issues
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {labels.length > 0 && (
            <div>
              <span style={lbl}>Labels</span>
              <div style={{ display:'flex', flexWrap:'wrap', gap:'0.35rem' }}>
                {labels.map(l => {
                  const active = labelIds.includes(l.id);
                  return (
                    <button key={l.id} type="button"
                      onClick={() => setLabelIds(prev => active ? prev.filter(id => id !== l.id) : [...prev, l.id])}
                      style={{ display:'flex', alignItems:'center', gap:5, padding:'3px 9px',
                               borderRadius:9999, border:`1.5px solid ${active ? l.color : '#E4E4E7'}`,
                               background: active ? `${l.color}18` : '#fff', cursor:'pointer',
                               fontSize:'0.73rem', fontWeight: active ? 700 : 400,
                               color: active ? l.color : '#374151' }}>
                      <span style={{ width:6, height:6, borderRadius:'50%', background:l.color }} />
                      {l.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div style={{ display:'flex', gap:'0.5rem', justifyContent:'flex-end',
                       paddingTop:'0.75rem', marginTop:'0.25rem',
                       borderTop:'1px solid #F4F4F5',
                       position:'sticky', bottom:0, background:'#fff',
                       zIndex:1 }}>
            <button type="button" onClick={onClose}
              style={{ padding:'0.45rem 1rem', border:'1px solid #E4E4E7', background:'#fff',
                       borderRadius:8, cursor:'pointer', fontWeight:600, fontSize:'0.83rem', color:'#374151' }}>
              Cancel
            </button>
            <button type="submit" disabled={saving}
              style={{ padding:'0.45rem 1.25rem', background: saving ? '#D1D5DB' : theme.primary,
                       color:'#fff', border:'none', borderRadius:8, fontWeight:700, fontSize:'0.83rem',
                       cursor: saving ? 'not-allowed' : 'pointer' }}>
              {saving ? 'Creating…' : 'Create Issue'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Table View ───────────────────────────────────────────────────────────────

function TableView({ issues, onOpen }) {
  const [sortCol, setSortCol] = useState('code');
  const [sortDir, setSortDir] = useState('asc');

  const sorted = [...issues].sort((a, b) => {
    let av = a[sortCol] ?? '', bv = b[sortCol] ?? '';
    if (sortCol === 'state')     { av = a.state_name ?? '';         bv = b.state_name ?? ''; }
    if (sortCol === 'assignees') { av = a.assignees?.[0]?.name ?? ''; bv = b.assignees?.[0]?.name ?? ''; }
    const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const handleSort = col => {
    if (col === sortCol) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const Th = ({ col, label, minW = 80 }) => (
    <th onClick={() => handleSort(col)}
      style={{ padding:'0.5rem 0.75rem', textAlign:'left', fontSize:'0.7rem', fontWeight:700,
               color:'#6B7280', textTransform:'uppercase', letterSpacing:'0.04em', cursor:'pointer',
               whiteSpace:'nowrap', minWidth:minW, background: sortCol===col ? '#F5F3FF' : '#F9FAFB',
               borderBottom:'2px solid #E4E4E7', userSelect:'none' }}>
      {label}{sortCol===col ? (sortDir==='asc' ? ' ↑' : ' ↓') : ''}
    </th>
  );

  return (
    <div style={{ padding:'0.75rem 1.5rem' }}>
      <table style={{ width:'100%', borderCollapse:'collapse', background:'#fff', borderRadius:10,
                      overflow:'hidden', border:'1px solid #E4E4E7', fontSize:'0.82rem' }}>
        <thead>
          <tr>
            <Th col="type"      label="Type"     minW={60}  />
            <Th col="code"      label="ID"       minW={70}  />
            <Th col="title"     label="Title"    minW={200} />
            <Th col="state"     label="State"    minW={100} />
            <Th col="priority"  label="Priority" minW={90}  />
            <Th col="assignees" label="Assignee" minW={100} />
            <Th col="due_date"  label="Due"      minW={100} />
            <Th col="estimate"  label="Est."     minW={60}  />
          </tr>
        </thead>
        <tbody>
          {sorted.map((issue, idx) => {
            const t = TYPE_META[issue.type]         || TYPE_META.task;
            const p = PRIORITY_META[issue.priority] || PRIORITY_META.none;
            const overdue = issue.due_date && !issue.completed_at && new Date(issue.due_date) < new Date();
            const rowBg   = idx % 2 === 0 ? '#fff' : '#FAFAFA';
            return (
              <tr key={issue.id} onClick={() => onOpen(issue.id)}
                style={{ background:rowBg, cursor:'pointer', borderBottom:'1px solid #F1F5F9' }}
                onMouseEnter={e => e.currentTarget.style.background='#F5F3FF'}
                onMouseLeave={e => e.currentTarget.style.background=rowBg}>
                <td style={{ padding:'0.45rem 0.75rem' }}>
                  <span style={{ background:t.bg, color:t.color, fontSize:'0.6rem', fontWeight:700,
                                 padding:'1px 6px', borderRadius:4 }}>{t.icon}</span>
                </td>
                <td style={{ padding:'0.45rem 0.75rem', fontFamily:'monospace', fontSize:'0.7rem',
                             color:'#0D9488', fontWeight:700, whiteSpace:'nowrap' }}>
                  {issue.code}
                </td>
                <td style={{ padding:'0.45rem 0.75rem', maxWidth:320 }}>
                  <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
                                 display:'block', color:'#111827', fontWeight:500 }}>
                    {issue.title}
                  </span>
                </td>
                <td style={{ padding:'0.45rem 0.75rem', whiteSpace:'nowrap' }}>
                  {issue.state_name && (
                    <span style={{ display:'inline-flex', alignItems:'center', gap:4, fontSize:'0.68rem',
                                   fontWeight:600, color:'#374151', background:'#F3F4F6',
                                   padding:'2px 7px', borderRadius:5 }}>
                      <span style={{ width:6, height:6, borderRadius:'50%', background:issue.state_color||'#9CA3AF' }} />
                      {issue.state_name}
                    </span>
                  )}
                </td>
                <td style={{ padding:'0.45rem 0.75rem', whiteSpace:'nowrap' }}>
                  <span style={{ color:p.color, fontWeight:700, fontSize:'0.75rem' }}>{p.icon} {p.label}</span>
                </td>
                <td style={{ padding:'0.45rem 0.75rem' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:3 }}>
                    {issue.assignees?.slice(0,3).map(a => <Avatar key={a.employee_id || a.id} name={a.name} photo={a.profile_photo} size={20} />)}
                    {issue.assignees?.length > 3 && (
                      <span style={{ fontSize:'0.65rem', color:'#9CA3AF' }}>+{issue.assignees.length-3}</span>
                    )}
                  </div>
                </td>
                <td style={{ padding:'0.45rem 0.75rem', whiteSpace:'nowrap', fontSize:'0.75rem',
                             color: overdue ? '#DC2626' : '#6B7280', fontWeight: overdue ? 700 : 400 }}>
                  {issue.due_date
                    ? new Date(issue.due_date).toLocaleDateString('en-GB',{day:'2-digit',month:'short'})
                    : '—'}
                </td>
                <td style={{ padding:'0.45rem 0.75rem', color:'#9CA3AF', fontSize:'0.75rem', textAlign:'center' }}>
                  {issue.estimate ?? '—'}
                </td>
              </tr>
            );
          })}
          {sorted.length === 0 && (
            <tr><td colSpan={8} style={{ textAlign:'center', padding:'3rem', color:'#9CA3AF', fontSize:'0.82rem' }}>
              No issues found
            </td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ── Timeline View ─────────────────────────────────────────────────────────────

function TimelineView({ issues, relations = [], onOpen }) {
  const DAY_W   = 32;
  const ROW_H   = 36;
  const DAYS    = 90;
  const ms1Day  = 86400000;
  const today   = new Date(); today.setHours(0,0,0,0);
  const startOf = new Date(today); startOf.setDate(today.getDate() - 7);

  const days = Array.from({ length:DAYS }, (_, i) => {
    const d = new Date(startOf); d.setDate(startOf.getDate() + i); return d;
  });

  const dayIdx = iso => {
    if (!iso) return null;
    const diff = Math.round((new Date(iso) - startOf) / ms1Day);
    return diff >= 0 && diff < DAYS ? diff : null;
  };

  const todayIdx = Math.round((today - startOf) / ms1Day);

  const months = [];
  days.forEach((d, i) => {
    const mo = d.toLocaleDateString('en-GB', { month:'short', year:'numeric' });
    if (!months.length || months[months.length-1].label !== mo) months.push({ label:mo, idx:i });
  });

  // Build a map of issueId → bar bounds for connector drawing
  const barMap = {};   // id → { x1, x2, cy, rowIndex }
  issues.forEach((issue, rowIndex) => {
    const si  = dayIdx(issue.start_date);
    const ei  = dayIdx(issue.due_date);
    const barL = si != null ? si : ei;
    const barW = si != null && ei != null ? Math.max(ei - si + 1, 1) : 1;
    if (barL != null) {
      const x1 = barL * DAY_W + 2;
      const x2 = x1 + barW * DAY_W - 4;
      const cy  = rowIndex * ROW_H + ROW_H / 2;
      barMap[issue.id] = { x1, x2, cy, rowIndex };
    }
  });

  // Build SVG connector paths for each relation
  const connectors = relations
    .map(rel => {
      const from = barMap[rel.from_issue_id];
      const to   = barMap[rel.to_issue_id];
      if (!from || !to) return null;

      const sx = from.x2;
      const sy = from.cy;
      const ex = to.x1;
      const ey = to.cy;
      const GAP = 20;

      let path;
      if (ex >= sx + GAP) {
        // Simple elbow: blocked bar is clearly to the right
        const midX = sx + (ex - sx) / 2;
        path = `M ${sx} ${sy} H ${midX} V ${ey} H ${ex}`;
      } else {
        // Bars overlap — detour past both bars, then enter blocked bar
        // from below (or above) to avoid crossing through it horizontally
        const detourX = Math.max(from.x2, to.x2) + GAP;
        if (to.rowIndex >= from.rowIndex) {
          // Blocked bar is on the same row or below — enter from below the bar
          const belowY = to.rowIndex * ROW_H + ROW_H - 3;
          path = `M ${sx} ${sy} H ${detourX} V ${belowY} H ${ex} V ${ey}`;
        } else {
          // Blocked bar is above — enter from above the bar
          const aboveY = to.rowIndex * ROW_H + 3;
          path = `M ${sx} ${sy} H ${detourX} V ${aboveY} H ${ex} V ${ey}`;
        }
      }

      return { path };
    })
    .filter(Boolean);

  const svgH = issues.length * ROW_H;
  const svgW = DAY_W * DAYS;
  const CONNECTOR_COLOR = '#F97316';

  return (
    <div style={{ padding:'0.75rem 1.5rem', overflowX:'auto' }}>
      <div style={{ minWidth:DAY_W*DAYS+220, background:'#fff', border:'1px solid #E4E4E7',
                    borderRadius:10, overflow:'hidden' }}>
        {/* Month row */}
        <div style={{ display:'flex', borderBottom:'1px solid #E4E4E7' }}>
          <div style={{ width:220, flexShrink:0, background:'#F9FAFB', borderRight:'1px solid #E4E4E7',
                        padding:'0.4rem 0.75rem', fontSize:'0.72rem', fontWeight:700, color:'#374151' }}>
            Issue
          </div>
          <div style={{ position:'relative', width:DAY_W*DAYS, flexShrink:0 }}>
            {months.map((m, mi) => (
              <span key={mi} style={{ position:'absolute', left:m.idx*DAY_W+4, top:4, fontSize:'0.65rem',
                                      fontWeight:700, color:'#6B7280', whiteSpace:'nowrap' }}>
                {m.label}
              </span>
            ))}
            <div style={{ height:24 }} />
          </div>
        </div>
        {/* Day header row */}
        <div style={{ display:'flex', borderBottom:'1px solid #E4E4E7', background:'#F9FAFB' }}>
          <div style={{ width:220, flexShrink:0, borderRight:'1px solid #E4E4E7' }} />
          <div style={{ display:'flex' }}>
            {days.map((d, i) => {
              const isToday = i === todayIdx;
              const isMon   = d.getDay() === 1;
              return (
                <div key={i}
                  style={{ width:DAY_W, flexShrink:0, textAlign:'center', fontSize:'0.58rem',
                           fontWeight: isToday ? 800 : 400,
                           color: isToday ? theme.primary : isMon ? '#374151' : '#C4C4C4',
                           padding:'2px 0', background: isToday ? `${theme.primary}10` : 'transparent',
                           borderRight: isMon ? '1px solid #E4E4E7' : '1px solid #F3F4F6' }}>
                  {isMon || isToday ? d.getDate() : ''}
                </div>
              );
            })}
          </div>
        </div>

        {/* Issue rows + SVG connector overlay */}
        <div style={{ position:'relative' }}>
          {issues.map(issue => {
            const si  = dayIdx(issue.start_date);
            const ei  = dayIdx(issue.due_date);
            const t   = TYPE_META[issue.type] || TYPE_META.task;
            const barL = si != null ? si : ei;
            const barW = si != null && ei != null ? Math.max(ei-si+1,1) : 1;
            const barColor = issue.state_color || t.color;
            return (
              <div key={issue.id}
                style={{ display:'flex', borderBottom:'1px solid #F9FAFB', minHeight:ROW_H }}
                onMouseEnter={e => e.currentTarget.style.background='#FAFAFA'}
                onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                <div onClick={() => onOpen(issue.id)}
                  style={{ width:220, flexShrink:0, borderRight:'1px solid #E4E4E7', padding:'0.3rem 0.75rem',
                           display:'flex', alignItems:'center', gap:'0.4rem', cursor:'pointer', overflow:'hidden' }}>
                  <span style={{ color:t.color, fontSize:'0.7rem', flexShrink:0 }}>{t.icon}</span>
                  <span style={{ fontSize:'0.65rem', color:'#0D9488', fontFamily:'monospace',
                                 fontWeight:700, flexShrink:0 }}>{issue.code}</span>
                  <span style={{ fontSize:'0.75rem', color:'#374151', overflow:'hidden',
                                 textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{issue.title}</span>
                </div>
                <div style={{ width:DAY_W*DAYS, flexShrink:0, position:'relative', height:ROW_H }}>
                  {/* Today line */}
                  <div style={{ position:'absolute', top:0, bottom:0, left:todayIdx*DAY_W+DAY_W/2,
                                width:1, background:`${theme.primary}40`, pointerEvents:'none' }} />
                  {/* Bar */}
                  {barL != null && (
                    <div onClick={() => onOpen(issue.id)}
                      title={`${issue.title}${issue.due_date ? ' · Due '+new Date(issue.due_date).toLocaleDateString('en-GB',{day:'2-digit',month:'short'}) : ''}`}
                      style={{ position:'absolute', top:8, height:20, cursor:'pointer',
                               left:barL*DAY_W+2, width:barW*DAY_W-4,
                               background:`${barColor}22`, border:`1.5px solid ${barColor}`, borderRadius:4,
                               display:'flex', alignItems:'center', padding:'0 6px', overflow:'hidden' }}>
                      <span style={{ fontSize:'0.6rem', fontWeight:700, color:barColor,
                                     whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                        {barW > 2 ? issue.title : ''}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {/* SVG connector overlay — sits over all rows, pointer-events:none */}
          {connectors.length > 0 && (
            <svg
              style={{
                position: 'absolute',
                top: 0,
                left: 220, // offset past the label column
                width: svgW,
                height: svgH,
                pointerEvents: 'none',
                overflow: 'visible',
              }}
            >
              <defs>
                <marker
                  id="arrow-dep"
                  markerWidth="6"
                  markerHeight="6"
                  refX="5"
                  refY="3"
                  orient="auto"
                >
                  <path d="M0,0 L0,6 L6,3 Z" fill={CONNECTOR_COLOR} />
                </marker>
              </defs>
              {connectors.map((c, i) => (
                <path
                  key={i}
                  d={c.path}
                  fill="none"
                  stroke={CONNECTOR_COLOR}
                  strokeWidth={1.5}
                  strokeDasharray="4 3"
                  markerEnd="url(#arrow-dep)"
                  opacity={0.75}
                />
              ))}
            </svg>
          )}
        </div>

        {issues.length === 0 && (
          <div style={{ textAlign:'center', padding:'3rem', color:'#9CA3AF', fontSize:'0.82rem' }}>
            No issues with dates to display on this timeline
          </div>
        )}
      </div>
    </div>
  );
}

// ── Calendar View ─────────────────────────────────────────────────────────────

function CalendarView({ issues, onOpen }) {
  const [calDate, setCalDate] = useState(() => { const d = new Date(); d.setDate(1); return d; });

  const year       = calDate.getFullYear();
  const month      = calDate.getMonth();
  const firstDow   = new Date(year, month, 1).getDay(); // 0=Sun
  const leadBlanks = firstDow === 0 ? 6 : firstDow - 1; // Mon-start grid
  const daysInMo   = new Date(year, month + 1, 0).getDate();
  const today      = new Date(); today.setHours(0,0,0,0);

  const issuesByDay = {};
  issues.forEach(issue => {
    if (!issue.due_date) return;
    const d = new Date(issue.due_date);
    if (d.getFullYear() === year && d.getMonth() === month) {
      const k = d.getDate();
      (issuesByDay[k] = issuesByDay[k] || []).push(issue);
    }
  });

  const cells = [...Array(leadBlanks).fill(null), ...Array.from({length:daysInMo},(_,i)=>i+1)];
  while (cells.length % 7 !== 0) cells.push(null);
  const weeks = [];
  for (let i = 0; i < cells.length; i+=7) weeks.push(cells.slice(i,i+7));

  const totalDueThisMo = Object.values(issuesByDay).reduce((s,a)=>s+a.length,0);

  return (
    <div style={{ padding:'0.75rem 1.5rem' }}>
      {/* Nav */}
      <div style={{ display:'flex', alignItems:'center', gap:'0.75rem', marginBottom:'0.875rem' }}>
        <button onClick={() => setCalDate(new Date(year, month-1, 1))}
          style={{ background:'none', border:'1px solid #E4E4E7', borderRadius:6,
                   padding:'4px 12px', cursor:'pointer', fontWeight:700, color:'#374151' }}>‹</button>
        <h3 style={{ margin:0, fontSize:'0.95rem', fontWeight:700, color:'#111827' }}>
          {calDate.toLocaleDateString('en-GB',{month:'long',year:'numeric'})}
        </h3>
        <button onClick={() => setCalDate(new Date(year, month+1, 1))}
          style={{ background:'none', border:'1px solid #E4E4E7', borderRadius:6,
                   padding:'4px 12px', cursor:'pointer', fontWeight:700, color:'#374151' }}>›</button>
        <span style={{ marginLeft:'auto', fontSize:'0.75rem', color:'#9CA3AF' }}>
          {totalDueThisMo} issue{totalDueThisMo !== 1 ? 's' : ''} due this month
        </span>
      </div>
      {/* Grid */}
      <div style={{ background:'#fff', border:'1px solid #E4E4E7', borderRadius:10, overflow:'hidden' }}>
        {/* Day headers */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', borderBottom:'1px solid #E4E4E7' }}>
          {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(d => (
            <div key={d} style={{ padding:'0.4rem', textAlign:'center', fontSize:'0.7rem',
                                   fontWeight:700, color:'#9CA3AF', background:'#F9FAFB' }}>
              {d}
            </div>
          ))}
        </div>
        {/* Weeks */}
        {weeks.map((week, wi) => (
          <div key={wi} style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)',
                                  borderBottom: wi < weeks.length-1 ? '1px solid #F3F4F6' : 'none' }}>
            {week.map((day, di) => {
              if (!day) return (
                <div key={di} style={{ background:'#FAFAFA', minHeight:90,
                                        borderRight: di<6 ? '1px solid #F3F4F6' : 'none' }} />
              );
              const cellDate  = new Date(year, month, day); cellDate.setHours(0,0,0,0);
              const isToday   = cellDate.getTime() === today.getTime();
              const dayIssues = issuesByDay[day] || [];
              return (
                <div key={di}
                  style={{ minHeight:90, padding:'0.35rem',
                           borderRight: di<6 ? '1px solid #F3F4F6' : 'none',
                           background: isToday ? `${theme.primary}06` : '#fff' }}>
                  <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:4 }}>
                    <span style={{ width:22, height:22, borderRadius:'50%', display:'flex',
                                   alignItems:'center', justifyContent:'center', fontSize:'0.72rem',
                                   fontWeight: isToday ? 800 : 500,
                                   background: isToday ? theme.primary : 'transparent',
                                   color: isToday ? '#fff' : '#374151' }}>
                      {day}
                    </span>
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', gap:2 }}>
                    {dayIssues.slice(0,3).map(issue => {
                      const t = TYPE_META[issue.type] || TYPE_META.task;
                      const c = issue.state_color || t.color;
                      return (
                        <div key={issue.id} onClick={() => onOpen(issue.id)} title={issue.title}
                          style={{ background:`${c}18`, borderLeft:`2.5px solid ${c}`, color:c,
                                   fontSize:'0.63rem', fontWeight:600, padding:'1px 5px',
                                   borderRadius:'0 3px 3px 0', cursor:'pointer',
                                   overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                          {t.icon} {issue.title}
                        </div>
                      );
                    })}
                    {dayIssues.length > 3 && (
                      <div style={{ fontSize:'0.6rem', color:'#9CA3AF', fontWeight:600, padding:'1px 4px' }}>
                        +{dayIssues.length-3} more
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Hierarchy View ────────────────────────────────────────────────────────────

function HierarchyView({ projectId, issues, onOpen }) {
  const [modules,    setModules]    = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [collapsed,  setCollapsed]  = useState({});

  useEffect(() => {
    pm.getModules(projectId).then(r => setModules(r.data || [])).catch(() => {});
    pm.getMilestones(projectId).then(r => setMilestones(r.data || [])).catch(() => {});
  }, [projectId]);

  const toggle = (key) => setCollapsed(p => ({ ...p, [key]: !p[key] }));

  // Build issue map for quick lookup
  const issueMap = {};
  issues.forEach(i => { issueMap[i.id] = i; });

  // Top-level issues (no parent)
  const topLevel = issues.filter(i => !i.parent_id);
  // Sub-issues keyed by parent id
  const childrenOf = {};
  issues.filter(i => i.parent_id).forEach(i => {
    (childrenOf[i.parent_id] = childrenOf[i.parent_id] || []).push(i);
  });

  const msById = {};
  milestones.forEach(m => { msById[m.id] = m; });

  // Issues per module (by module_id)
  const issuesByModule = {};
  modules.forEach(m => { issuesByModule[m.id] = []; });
  const unmoduled = [];
  topLevel.forEach(issue => {
    if (!issue.module_ids?.length) { unmoduled.push(issue); return; }
    issue.module_ids.forEach(mid => {
      if (issuesByModule[mid]) issuesByModule[mid].push(issue);
    });
  });

  const rowStyle = (depth) => ({
    display: 'flex', alignItems: 'center', gap: 8,
    padding: `6px 12px 6px ${12 + depth * 20}px`,
    borderBottom: '1px solid #F3F4F6', cursor: 'pointer',
    background: '#fff',
  });

  const IssueRow = ({ issue, depth = 0 }) => {
    const t = TYPE_META[issue.type] || TYPE_META.task;
    const p = PRIORITY_META[issue.priority] || PRIORITY_META.none;
    const kids = childrenOf[issue.id] || [];
    const key = `issue-${issue.id}`;
    const open = !collapsed[key];
    const msBadges = (issue.milestone_ids || []).map(mid => msById[mid]).filter(Boolean);

    return (
      <>
        <div style={rowStyle(depth)}
          onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'}
          onMouseLeave={e => e.currentTarget.style.background = '#fff'}
          onClick={() => onOpen(issue.id)}>
          {kids.length > 0 && (
            <span onClick={e => { e.stopPropagation(); toggle(key); }}
              style={{ fontSize: 10, color: '#9CA3AF', cursor: 'pointer', width: 14, flexShrink: 0 }}>
              {open ? '▾' : '▸'}
            </span>
          )}
          {!kids.length && <span style={{ width: 14, flexShrink: 0 }} />}
          <span style={{ background: t.bg, color: t.color, fontSize: '0.6rem', fontWeight: 700,
                         padding: '1px 6px', borderRadius: 4, flexShrink: 0 }}>{t.icon}</span>
          <span style={{ fontSize: '0.65rem', color: '#0D9488', fontFamily: 'monospace',
                         fontWeight: 700, flexShrink: 0 }}>{issue.code}</span>
          <span style={{ flex: 1, fontSize: '0.82rem', color: '#111827', fontWeight: 500,
                         overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {issue.title}
          </span>
          {msBadges.map(ms => (
            <span key={ms.id} style={{ fontSize: '0.6rem', padding: '1px 7px', borderRadius: 99,
                                       background: `${ms.color}22`, color: ms.color, fontWeight: 600,
                                       flexShrink: 0, maxWidth: 100, overflow: 'hidden',
                                       textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              🎯 {ms.name}
            </span>
          ))}
          {issue.due_date && (
            <span style={{ fontSize: '0.65rem', color: '#9CA3AF', flexShrink: 0 }}>
              {new Date(issue.due_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
            </span>
          )}
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: p.color, flexShrink: 0 }} />
          <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
            {issue.assignees?.slice(0, 2).map(a => <Avatar key={a.employee_id} name={a.name} photo={a.profile_photo} size={18} />)}
          </div>
        </div>
        {open && kids.map(child => <IssueRow key={child.id} issue={child} depth={depth + 1} />)}
      </>
    );
  };

  const SectionHeader = ({ label, color = '#6B7280', count, sectionKey, icon }) => (
    <div onClick={() => toggle(sectionKey)}
      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
               background: '#F9FAFB', borderBottom: '1px solid #E5E7EB',
               borderLeft: `3px solid ${color}`, cursor: 'pointer', userSelect: 'none' }}>
      <span style={{ fontSize: 10, color: '#9CA3AF', width: 12 }}>
        {collapsed[sectionKey] ? '▸' : '▾'}
      </span>
      {icon && <span>{icon}</span>}
      <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#374151', flex: 1 }}>{label}</span>
      <span style={{ fontSize: '0.68rem', background: `${color}20`, color,
                     padding: '1px 8px', borderRadius: 99, fontWeight: 700 }}>{count}</span>
    </div>
  );

  return (
    <div style={{ padding: '1rem 1.5rem' }}>
      <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ display: 'grid', gridTemplateColumns: '14px 60px 70px 1fr auto auto 14px 40px',
                      gap: 8, padding: '6px 12px', background: '#F3F4F6',
                      borderBottom: '2px solid #E5E7EB', fontSize: '0.65rem',
                      fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase' }}>
          <span /><span>Type</span><span>ID</span><span>Title</span>
          <span>Milestone</span><span>Due</span><span /><span>Assignee</span>
        </div>

        {/* Modules */}
        {modules.map(mod => {
          const modIssues = issuesByModule[mod.id] || [];
          if (!modIssues.length) return null;
          const key = `mod-${mod.id}`;
          return (
            <div key={mod.id}>
              <SectionHeader label={mod.name} color='#14B8A6' count={modIssues.length} sectionKey={key} icon='▦' />
              {!collapsed[key] && modIssues.map(issue => (
                <IssueRow key={issue.id} issue={issue} depth={0} />
              ))}
            </div>
          );
        })}

        {/* Unmoduled */}
        {unmoduled.length > 0 && (
          <div>
            <SectionHeader label='No Module' color='#9CA3AF' count={unmoduled.length} sectionKey='__nomod' icon='◈' />
            {!collapsed['__nomod'] && unmoduled.map(issue => (
              <IssueRow key={issue.id} issue={issue} depth={0} />
            ))}
          </div>
        )}

        {issues.length === 0 && (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#9CA3AF', fontSize: '0.85rem' }}>
            No issues yet
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Issues Component ────────────────────────────────────────────────────

export default function Issues({ project, onRefresh: _onRefresh }) {
  const { user } = useAuth();
  const [issues, setIssues]     = useState([]);
  const [states, setStates]     = useState([]);
  const [labels, setLabels]     = useState([]);
  const [members, setMembers]   = useState([]);
  const [loading, setLoading]   = useState(true);

  // PM/TL of this project can fully manage issues; others are restricted
  const canManage = useMemo(() =>
    PM_MANAGE_ROLES.includes(user?.role) ||
    (user?.emp_id != null && project?.manager_id != null &&
     Number(user.emp_id) === Number(project.manager_id)) ||
    members.some(m => Number(m.employee_id) === Number(user?.emp_id) &&
      ['project_manager','scrum_master','tech_lead','team_lead'].includes(m.role)),
  [user, project, members]);

  const currentEmpId = user?.emp_id ? Number(user.emp_id) : null;
  const location = useLocation();
  const [view, setView]         = useState('list');
  const [groupBy, setGroupBy]   = useState('state');
  const [selectedIssueId, setSelectedIssueId] = useState(() => {
    const p = new URLSearchParams(location.search);
    const id = p.get('open');
    return id ? Number(id) : null;
  });
  const [creating, setCreating] = useState(false);
  const [dragInfo, setDragInfo] = useState(null);
  const [dragOverState, setDragOverState] = useState(null);
  const [relations, setRelations] = useState([]);

  // Filters
  const [filterPriority, setFilterPriority] = useState('');
  const [filterType, setFilterType]         = useState('');
  const [filterAssignee, setFilterAssignee] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterPriority) params.priority = filterPriority;
      if (filterType)     params.type     = filterType;
      if (filterAssignee) params.assignee_id = filterAssignee;
      const [iRes, sRes, lRes, mRes] = await Promise.all([
        pm.getIssues(project.id, params),
        pm.getStates(project.id),
        pm.getLabels(project.id),
        pm.getAssignableEmployees(project.id),
      ]);
      setIssues(iRes.data || []);
      setStates(sRes.data || []);
      setLabels(lRes.data || []);
      setMembers(mRes.data || []);
    } catch {} finally { setLoading(false); }
  }, [project.id, filterPriority, filterType, filterAssignee]);

  useEffect(() => { load(); }, [load]);

  // Auto-open issue from ?open=<issue_id> (notification deep link)
  useEffect(() => {
    const openId = new URLSearchParams(location.search).get('open');
    if (openId) setSelectedIssueId(Number(openId));
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch relations whenever timeline view is active
  useEffect(() => {
    if (view !== 'timeline') return;
    pm.getProjectRelations(project.id)
      .then(r => setRelations(r.data || []))
      .catch(() => {});
  }, [view, project.id]);

  // Drag & drop for board
  const deleteIssue = async (issueId) => {
    if (!window.confirm('Delete this issue? This cannot be undone.')) return;
    try { await pm.deleteIssue(issueId); load(); } catch {}
  };

  const handleDragStart = (e, issue) => {
    setDragInfo(issue);
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleDrop = async (e, targetState) => {
    e.preventDefault();
    setDragOverState(null);
    if (!dragInfo || dragInfo.state_id === targetState.id) { setDragInfo(null); return; }
    try {
      await pm.updateIssue(dragInfo.id, { state_id: targetState.id });
      load();
    } catch {}
    setDragInfo(null);
  };

  // Group issues by state for board view
  const issuesByState = states.map(s => ({
    state: s,
    issues: issues.filter(i => i.state_id === s.id),
  }));

  const GROUP_META = {
    backlog:    { border:'#E4E4E7', bg:'#F9FAFB' },
    unstarted:  { border:'#E4E4E7', bg:'#F9FAFB' },
    started:    { border:'#BFDBFE', bg:'#EFF6FF' },
    completed:  { border:'#BBF7D0', bg:'#F0FDF4' },
    cancelled:  { border:'#E4E4E7', bg:'#F9FAFB' },
  };

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:300, color:'#6B7280' }}>
      <div style={{ width:32, height:32, border:'3px solid #E4E4E7', borderTopColor:theme.primary,
                    borderRadius:'50%', animation:'spin 0.8s linear infinite' }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  return (
    <div style={{ height:'100%', display:'flex', flexDirection:'column' }}>

      {/* ── Toolbar ── */}
      <div style={{ padding:'0.75rem 1.5rem', display:'flex', alignItems:'center', gap:'0.625rem',
                    flexWrap:'wrap', background:'#fff', borderBottom:'1px solid #F1F5F9' }}>

        {/* View toggle */}
        <div style={{ display:'flex', background:'#F4F4F5', borderRadius:8, padding:2, gap:1 }}>
          {[['list','☰ List'],['board','▦ Board'],['table','⊞ Table'],['timeline','↔ Timeline'],['calendar','📅 Calendar'],['hierarchy','⊹ Hierarchy']].map(([k,label]) => (
            <button key={k} onClick={() => setView(k)}
              style={{ padding:'4px 12px', borderRadius:6, border:'none', cursor:'pointer',
                       fontSize:'0.75rem', fontWeight: view===k ? 700 : 400,
                       background: view===k ? '#fff' : 'transparent',
                       color: view===k ? '#111827' : '#6B7280',
                       boxShadow: view===k ? '0 1px 4px rgba(0,0,0,0.1)' : 'none' }}>
              {label}
            </button>
          ))}
        </div>

        {/* Group-by (list view only) */}
        {view === 'list' && (
          <select value={groupBy} onChange={e => setGroupBy(e.target.value)}
            style={{ padding:'4px 8px', border:'1px solid #E4E4E7', borderRadius:7, fontSize:'0.75rem',
                     background: groupBy !== 'state' ? '#F5F3FF' : '#fff', cursor:'pointer',
                     color: groupBy !== 'state' ? theme.primary : '#6B7280' }}>
            <option value="state">Group: Status</option>
            <option value="priority">Group: Priority</option>
            <option value="type">Group: Type</option>
            <option value="assignee">Group: Assignee</option>
            <option value="none">No Grouping</option>
          </select>
        )}

        {/* Filters */}
        <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)}
          style={{ padding:'4px 8px', border:'1px solid #E4E4E7', borderRadius:7, fontSize:'0.75rem',
                   background:'#fff', cursor:'pointer', color: filterPriority ? theme.primary : '#6B7280' }}>
          <option value="">Priority</option>
          {Object.entries(PRIORITY_META).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={filterType} onChange={e => setFilterType(e.target.value)}
          style={{ padding:'4px 8px', border:'1px solid #E4E4E7', borderRadius:7, fontSize:'0.75rem',
                   background:'#fff', cursor:'pointer', color: filterType ? theme.primary : '#6B7280' }}>
          <option value="">Type</option>
          {Object.entries(TYPE_META).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={filterAssignee} onChange={e => setFilterAssignee(e.target.value)}
          style={{ padding:'4px 8px', border:'1px solid #E4E4E7', borderRadius:7, fontSize:'0.75rem',
                   background:'#fff', cursor:'pointer', color: filterAssignee ? theme.primary : '#6B7280' }}>
          <option value="">Assignee</option>
          {members.map(m => <option key={m.employee_id} value={m.employee_id}>{m.name}</option>)}
        </select>

        {(filterPriority || filterType || filterAssignee) && (
          <button onClick={() => { setFilterPriority(''); setFilterType(''); setFilterAssignee(''); }}
            style={{ padding:'4px 8px', border:'none', background:'#FEE2E2', color:'#DC2626',
                     borderRadius:6, cursor:'pointer', fontSize:'0.72rem', fontWeight:600 }}>
            ✕ Clear
          </button>
        )}

        <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:'0.5rem' }}>
          <span style={{ fontSize:'0.75rem', color:'#9CA3AF' }}>{issues.length} issue{issues.length !== 1 ? 's' : ''}</span>
          <button onClick={() => setCreating(true)}
            style={{ padding:'0.42rem 1rem', background:theme.primary, color:'#fff', border:'none',
                     borderRadius:8, cursor:'pointer', fontWeight:700, fontSize:'0.8rem',
                     boxShadow:`0 2px 6px ${theme.primary}40` }}>
            + New Issue
          </button>
        </div>
      </div>

      {/* ── Content ── */}
      <div style={{ flex:1, overflow:'auto' }}>

        {/* LIST VIEW */}
        {view === 'list' && (() => {
          // Build groups based on groupBy selection
          let groups = [];
          if (groupBy === 'state') {
            groups = states.map(s => ({
              key:    String(s.id),
              label:  s.name,
              dot:    s.color,
              issues: issues.filter(i => i.state_id === s.id),
              topBorder: s.color,
            }));
            const unstatedIssues = issues.filter(i => !i.state_id);
            if (unstatedIssues.length) groups.push({ key:'__none', label:'No Status', dot:'#D1D5DB', issues: unstatedIssues, topBorder:'#D1D5DB' });
          } else if (groupBy === 'priority') {
            groups = Object.entries(PRIORITY_META).map(([k, v]) => ({
              key: k, label: v.label, dot: v.color,
              issues: issues.filter(i => (i.priority || 'none') === k),
              topBorder: v.color,
            }));
          } else if (groupBy === 'type') {
            groups = Object.entries(TYPE_META).map(([k, v]) => ({
              key: k, label: v.label, dot: v.color,
              issues: issues.filter(i => (i.type || 'task') === k),
              topBorder: v.color,
            }));
          } else if (groupBy === 'assignee') {
            const seen = new Set();
            groups = [];
            issues.forEach(i => {
              const a = i.assignees?.[0];
              const key = a ? String(a.employee_id) : '__none';
              if (!seen.has(key)) {
                seen.add(key);
                groups.push({ key, label: a?.name || 'Unassigned', dot: a ? avatarColor(a.name) : '#D1D5DB',
                              issues: [], topBorder: '#E4E4E7' });
              }
            });
            groups.forEach(g => { g.issues = issues.filter(i => (i.assignees?.[0] ? String(i.assignees[0].employee_id) : '__none') === g.key); });
          } else {
            // no grouping — single flat group
            groups = [{ key:'all', label:`All Issues`, dot: theme.primary, issues, topBorder: theme.primary }];
          }

          return (
            <div>
              {groups.filter(g => g.issues.length > 0).map(g => (
                <div key={g.key} style={{ marginBottom:'0.25rem' }}>
                  <div style={{ padding:'0.5rem 1rem', display:'flex', alignItems:'center', gap:'0.5rem',
                                background:'#F8FAFC', borderTop:`2px solid ${g.topBorder}`, borderBottom:'1px solid #F1F5F9' }}>
                    <span style={{ width:8, height:8, borderRadius:'50%', background:g.dot, flexShrink:0 }} />
                    <span style={{ fontSize:'0.78rem', fontWeight:700, color:'#374151' }}>{g.label}</span>
                    <span style={{ fontSize:'0.68rem', background:`${g.dot}20`, color:g.dot,
                                   padding:'0 6px', borderRadius:9999, fontWeight:700 }}>
                      {g.issues.length}
                    </span>
                  </div>
                  {g.issues.map(issue => (
                    <IssueRow key={issue.id} issue={issue} onOpen={i => setSelectedIssueId(i.id)} onDelete={canManage ? deleteIssue : null} canManage={canManage} />
                  ))}
                </div>
              ))}
              {issues.length === 0 && (
                <div style={{ textAlign:'center', padding:'4rem 2rem', color:'#9CA3AF' }}>
                  <div style={{ fontSize:'2.5rem', marginBottom:'0.75rem' }}>◈</div>
                  <p style={{ fontWeight:600, color:'#374151', margin:'0 0 0.5rem' }}>No issues yet</p>
                  <p style={{ margin:'0 0 1rem', fontSize:'0.875rem' }}>Create your first issue to get started.</p>
                  <button onClick={() => setCreating(true)}
                    style={{ padding:'0.5rem 1.25rem', background:theme.primary, color:'#fff', border:'none',
                             borderRadius:8, cursor:'pointer', fontWeight:700, fontSize:'0.875rem' }}>
                    + Create Issue
                  </button>
                </div>
              )}
            </div>
          );
        })()}

        {/* TABLE VIEW */}
        {view === 'table' && (
          <TableView issues={issues} onOpen={id => setSelectedIssueId(id)} />
        )}

        {/* TIMELINE VIEW */}
        {view === 'timeline' && (
          <TimelineView issues={issues} relations={relations} onOpen={id => setSelectedIssueId(id)} />
        )}

        {/* CALENDAR VIEW */}
        {view === 'calendar' && (
          <CalendarView issues={issues} onOpen={id => setSelectedIssueId(id)} />
        )}

        {/* HIERARCHY VIEW */}
        {view === 'hierarchy' && (
          <HierarchyView projectId={project.id} issues={issues} onOpen={id => setSelectedIssueId(id)} />
        )}

        {/* BOARD VIEW */}
        {view === 'board' && (
          <div style={{ display:'flex', gap:'0.75rem', padding:'1rem 1.5rem', alignItems:'flex-start',
                        minHeight:'100%', height:'calc(100vh - 200px)' }}>
            {states.map(s => {
              const col = issuesByState.find(g => g.state.id === s.id);
              const colIssues = col?.issues || [];
              const isDragTarget = dragOverState === s.id;
              const gm = GROUP_META[s.group] || GROUP_META.unstarted;
              return (
                <div key={s.id} style={{ width:280, flexShrink:0, display:'flex', flexDirection:'column' }}
                  onDragOver={e => { e.preventDefault(); setDragOverState(s.id); }}
                  onDragLeave={() => setDragOverState(null)}
                  onDrop={e => handleDrop(e, s)}>
                  {/* Column header */}
                  <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', marginBottom:'0.6rem',
                                padding:'0.4rem 0.5rem', borderRadius:8,
                                background: isDragTarget ? `${s.color}10` : 'transparent' }}>
                    <span style={{ width:8, height:8, borderRadius:'50%', background:s.color, flexShrink:0 }} />
                    <span style={{ fontSize:'0.78rem', fontWeight:700, color:'#374151', flex:1 }}>{s.name.toUpperCase()}</span>
                    <span style={{ fontSize:'0.68rem', background:`${s.color}20`, color:s.color,
                                   padding:'0 6px', borderRadius:9999, fontWeight:700 }}>
                      {colIssues.length}
                    </span>
                  </div>
                  {/* Cards */}
                  <div style={{ flex:1, background: isDragTarget ? `${s.color}08` : gm.bg,
                                borderRadius:10, padding:'0.5rem', minHeight:120,
                                border:`1.5px solid ${isDragTarget ? s.color : gm.border}`,
                                transition:'background 0.15s, border-color 0.15s' }}>
                    {colIssues.map(issue => (
                      <IssueCard key={issue.id} issue={issue}
                        onDragStart={handleDragStart}
                        onClick={i => setSelectedIssueId(i.id)}
                        isDragging={dragInfo?.id === issue.id} />
                    ))}
                    {colIssues.length === 0 && (
                      <div style={{ textAlign:'center', padding:'1.5rem 0.5rem', color:'#C7C7CC',
                                    fontSize:'0.75rem', borderRadius:6,
                                    border: isDragTarget ? `2px dashed ${s.color}` : '2px dashed transparent' }}>
                        {isDragTarget ? 'Drop here' : 'No issues'}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Issue Detail Panel ── */}
      {selectedIssueId && (
        <IssueDetailPanel
          issueId={selectedIssueId}
          projectId={project.id}
          states={states}
          labels={labels}
          members={members}
          allIssues={issues}
          onClose={() => setSelectedIssueId(null)}
          onRefresh={load}
          canManage={canManage}
          currentEmpId={currentEmpId}
        />
      )}

      {/* ── Create Issue Modal ── */}
      {creating && (
        <CreateIssueModal
          projectId={project.id}
          states={states}
          labels={labels}
          members={members}
          allIssues={issues}
          onClose={() => setCreating(false)}
          onCreated={load}
          canManage={canManage}
          currentEmpId={currentEmpId}
        />
      )}
    </div>
  );
}
